/**
 * Curtain Customizer PDP block — form wizard
 * Handles step navigation, form validation, price calculation, and cart integration
 */

(function (global) {
  const LINE_ATTR_COUNT_KEY = '_ccpdp_line_attr_count';
  const LINE_ATTR_KEYS_KEY = '_ccpdp_line_attr_keys';
  const KNOWN_VISIBLE_SUFFIXES = [
    'Product',
    'Qty',
    'Room',
    'Panels',
    'Fullness',
    'Fabric Width',
    'Extra Length',
    'Curtain Drop',
    'Track Hooks',
    'Eyelet Color',
    'Middle of Curtain',
    'Heading Style',
    'Curtain Size'
  ];
  const KNOWN_HIDDEN_SUFFIXES = [
    'Heading Style',
    'Heading Style ID',
    'Fullness ID',
    'Fullness',
    'Total Width with Fullness (m)',
    'Track Hook Count',
    'Curtainry Wave Rail',
    'Fabric Width from Hooks (m)',
    'Eyelet Color ID',
    'Join Type',
    'Track or Rod Width'
  ];

  function hasUsableProperties(item) {
    const properties = item && item.properties;
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return false;
    return Object.keys(properties).some((key) => {
      const value = properties[key];
      return value != null && String(value).trim() !== '';
    });
  }

  function attributeKeyForProperty(lineNum, propertyKey) {
    if (String(propertyKey).charAt(0) === '_') {
      return `_Line ${lineNum} ${String(propertyKey).slice(1)}`;
    }
    return `Line ${lineNum} ${propertyKey}`;
  }

  function collectKeysToClear(existingAttributes) {
    const keys = new Set();
    const attributes = existingAttributes && typeof existingAttributes === 'object' ? existingAttributes : {};
    const prevCount = parseInt(attributes[LINE_ATTR_COUNT_KEY], 10);
    const maxLines = Number.isFinite(prevCount) && prevCount > 0 ? prevCount : 0;

    for (let lineNum = 1; lineNum <= maxLines; lineNum += 1) {
      KNOWN_VISIBLE_SUFFIXES.forEach((suffix) => keys.add(`Line ${lineNum} ${suffix}`));
      KNOWN_HIDDEN_SUFFIXES.forEach((suffix) => keys.add(`_Line ${lineNum} ${suffix}`));
    }

    const stored = attributes[LINE_ATTR_KEYS_KEY];
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          parsed.forEach((key) => {
            if (key) keys.add(String(key));
          });
        }
      } catch (error) {
        /* ignore malformed previous key lists */
      }
    }

    return keys;
  }

  async function syncCartLineAttributes(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const existingAttributes = cart.attributes && typeof cart.attributes === 'object' && !Array.isArray(cart.attributes)
      ? cart.attributes
      : {};
    const keysToClear = collectKeysToClear(existingAttributes);
    const attributes = {};

    Object.keys(existingAttributes).forEach((key) => {
      const value = existingAttributes[key];
      if (value == null) return;
      attributes[key] = String(value);
    });

    keysToClear.forEach((key) => {
      attributes[key] = '';
    });

    const writtenKeys = [];
    let lineNum = 0;

    cart.items.forEach((item) => {
      if (!hasUsableProperties(item)) return;
      lineNum += 1;

      const setAttr = (key, value) => {
        if (value == null) return;
        const text = String(value).trim();
        if (!text) return;
        attributes[key] = text;
        writtenKeys.push(key);
      };

      const productTitle = item.product_title || item.title || '';
      setAttr(`Line ${lineNum} Product`, productTitle);
      if (item.quantity != null) setAttr(`Line ${lineNum} Qty`, String(item.quantity));

      Object.entries(item.properties).forEach(([propertyKey, value]) => {
        setAttr(attributeKeyForProperty(lineNum, propertyKey), value);
      });
    });

    if (lineNum === 0 && keysToClear.size === 0 && !existingAttributes[LINE_ATTR_COUNT_KEY]) {
      return;
    }

    attributes[LINE_ATTR_COUNT_KEY] = String(lineNum);
    attributes[LINE_ATTR_KEYS_KEY] = JSON.stringify(writtenKeys);

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes })
    });

    if (!response.ok) {
      throw new Error(`Cart attribute sync failed (${response.status})`);
    }
  }

  global.CurtainryPdp = global.CurtainryPdp || {};
  global.CurtainryPdp.syncCartLineAttributes = syncCartLineAttributes;
})(window);

class CurtainCustomizerPdp {
  constructor(blockId) {
    this.blockId = blockId;
    this.currentStep = 1;
    this.totalSteps = 4;
    this.isAddingToCart = false;
    this.isAddingCushionFromSuccess = false;
    this.isStep4Completed = false;
    this.maxWidthMeters = 8;
    this.maxDropMeters = 2.7;
    this.standardMaxDropMeters = 2.7;
    this.fabricRollWidthMeters = 2.8;
    this.extraLengthHemMeters = 0.5;
    this.extraLengthWidthToleranceMeters = 0.10;
    this.waveForcedFullness = 2.5;
    this.maxFabricWidthCm = 2000;
    this.lastDisplayedPriceText = '';
    this.headingNoticeType = '';
    this.cushionUpsell = null;
    this.cushionsAddedWithCurtain = false;
    this.waveFullnessNote = '';
    this.waveFullnessConfirm = '';
    this.waterfallForcedNote = '';
    this.waveHooksLabel = '';
    this.waveHooksDescription = '';
    this.waveHooksMismatchWarning = '';
    this.waveHooksConfirmLabel = '';
    this.waveHooksCalculatedLabel = '';
    this.waveHooksRangeError = '';
    this.waveWidthMismatchTolerance = 0;
    this.reviewPreviewImages = {
      single: '',
      pair: '',
      waterfall: ''
    };
    this.reviewOverlayPositions = {};
    this.closeTotalWidthTooltip = () => {};
    this.formData = {
      headingStyle: null,
      width: null,
      drop: null,
      panelType: null,
      fullness: null,
      joinType: null,
      roomName: null,
      addScatterCushions: false,
      trackHookCount: null,
      waveHooksMismatchAcknowledged: false,
      waveRailChoice: null,
      orderingCurtainryWaveRail: false
    };
    
    this.init();
  }

  init() {
    this.container = document.querySelector(`[data-ccpdp-id="${this.blockId}"]`);
    if (!this.container) return;

    this.form = this.container.querySelector(`#curtain-customizer-pdp-form-${this.blockId}`);
    if (!this.form) return;
    this.waveFullnessNote = '';
    this.waveFullnessConfirm = '';
    this.waterfallForcedNote = this.form.dataset.waterfallForcedNote || 'Waterfall heading uses fixed panel settings: Panel Type is set to Pair and Middle of Curtain is set to Joined.';
    this.waveHooksLabel = this.form.dataset.waveHooksLabel || 'Wave rail details';
    this.waveHooksDescription = this.form.dataset.waveHooksDescription || '';
    this.waveHooksMismatchWarning = this.form.dataset.waveHooksMismatchWarning || 'The width from your hook count does not match your track width and fullness. Please double-check your hook count, or contact us if your measurements are correct.';
    this.waveHooksConfirmLabel = this.form.dataset.waveHooksConfirmLabel || 'I have double-checked my hook count';
    this.waveHooksCalculatedLabel = this.form.dataset.waveHooksCalculatedLabel || 'Fabric width from hooks';
    this.waveHooksRangeError = this.form.dataset.waveHooksRangeError || 'Hook count must give a fabric width between 1.00 m and 20.00 m.';
    this.waveHooksEvenError = this.form.dataset.waveHooksEvenError || 'Enter an even number of hooks.';
    const mismatchTolerancePct = parseFloat(this.form.dataset.waveHooksMismatchTolerancePercent || 0);
    this.waveWidthMismatchTolerance = Number.isFinite(mismatchTolerancePct) && mismatchTolerancePct >= 0
      ? mismatchTolerancePct / 100
      : 0;
    this.reviewPreviewImages.single = this.form.dataset.reviewPreviewSingleImage || '';
    this.reviewPreviewImages.pair = this.form.dataset.reviewPreviewPairImage || '';
    this.reviewPreviewImages.waterfall = this.form.dataset.reviewPreviewWaterfallImage || '';
    this.reviewOverlayPositions = {
      single: {
        drop: { x: this.form.dataset.overlaySingleDropX, y: this.form.dataset.overlaySingleDropY },
        topLeft: { x: this.form.dataset.overlaySingleTopLeftX, y: this.form.dataset.overlaySingleTopLeftY },
        topRight: { x: this.form.dataset.overlaySingleTopRightX, y: this.form.dataset.overlaySingleTopRightY }
      },
      pair: {
        drop: { x: this.form.dataset.overlayPairDropX, y: this.form.dataset.overlayPairDropY },
        topLeft: { x: this.form.dataset.overlayPairTopLeftX, y: this.form.dataset.overlayPairTopLeftY },
        topRight: { x: this.form.dataset.overlayPairTopRightX, y: this.form.dataset.overlayPairTopRightY }
      },
      waterfall: {
        drop: { x: this.form.dataset.overlayWaterfallDropX, y: this.form.dataset.overlayWaterfallDropY },
        topLeft: { x: this.form.dataset.overlayWaterfallTopLeftX, y: this.form.dataset.overlayWaterfallTopLeftY },
        topRight: { x: this.form.dataset.overlayWaterfallTopRightX, y: this.form.dataset.overlayWaterfallTopRightY }
      }
    };
    
    // Check if already initialized to prevent duplicate event listeners
    if (this.form.dataset.initialized === 'true') {
      console.warn(`Curtain customizer for block ${this.blockId} already initialized`);
      return;
    }
    this.form.dataset.initialized = 'true';

    console.info(
      `Curtain Customizer PDP: init (block ${this.blockId}, handle: ${this.form.dataset.productHandle || 'pending'})`
    );
    
    this.steps = this.container.querySelectorAll('.curtain-customizer-pdp__step');
    this.stepIndicators = this.container.querySelectorAll('.step-indicator__item');
    
    // Sort heading/fullness options by desired display order.
    this.sortHeadingOptions();
    this.sortFullnessOptions();
    this.updateOptionGridLayouts();
    this.setupTotalWidthTooltip();
    
    // Set default selections
    this.setDefaultFullness();
    this.setDefaultHeadingStyle();
    
    // Get variant data
    const variantIdInput = this.form.querySelector('input[name="variant_id"]');
    const variantPriceInput = this.form.querySelector('input[name="variant_price"]');
    const shopCurrencyInput = this.form.querySelector('input[name="shop_currency"]');
    this.variantId = variantIdInput ? (variantIdInput.value || variantIdInput.dataset.variantId || null) : null;
    this.variantPrice = variantPriceInput ? parseFloat(variantPriceInput.value || variantPriceInput.dataset.variantPrice || 0) : 0;
    this.shopCurrency = shopCurrencyInput ? shopCurrencyInput.value : 'USD';
    
    // Load variants mapping for width (+ optional heading style) variant selection
    this.hasHeadingVariants = this.form.dataset.hasHeadingVariants === 'true';
    this.variantsMap = {};
    this.variantsData = {};
    this.availableWidthsByHeading = {};
    this.variantsLoadPromise = null;
    this.variantsLoaded = false;
    this.variantsFetchSettled = false;
    this.productHeadingKeys = new Set();
    this.shopifyHeadingOptions = [];
    const headingOptionsScript = this.container.querySelector(`#heading-style-options-${this.blockId}`);
    if (headingOptionsScript) {
      try {
        const options = JSON.parse(headingOptionsScript.textContent);
        if (Array.isArray(options)) {
          this.shopifyHeadingOptions = options.filter(Boolean);
        }
      } catch (e) {
        console.error('Error parsing heading style options:', e);
      }
    }

    this.productHandle = this.form.dataset.productHandle || '';
    this.storefrontAccessToken = (this.form.dataset.storefrontAccessToken || '').trim();
    this.widthOptionIndex = parseInt(this.form.dataset.widthOptionIndex, 10) || 0;
    this.headingOptionIndex = parseInt(this.form.dataset.headingOptionIndex, 10) || 0;
    this.expectedVariantsCount = parseInt(this.form.dataset.variantsCount, 10) || 0;
    this.resolveProductHandle();

    const debugScript = this.container.querySelector(`#ccpdp-product-debug-${this.blockId}`);
    if (debugScript) {
      try {
        const debugData = JSON.parse(debugScript.textContent);
        if (!this.productHandle && debugData.selected_product_handle) {
          this.productHandle = debugData.selected_product_handle;
        }
        if (debugData.variants_count && debugData.variants_count > this.expectedVariantsCount) {
          this.expectedVariantsCount = debugData.variants_count;
        }
        if (!this.variantId && debugData.has_variant === false) {
          console.warn('No variant found, but product has variants:', debugData.variants_count);
        }
      } catch (e) {
        console.error('Error parsing debug data:', e);
      }
    }

    if (!this.productHandle && typeof window.Shopify !== 'undefined') {
      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct && metaProduct.handle) {
        this.productHandle = metaProduct.handle;
      }
    }

    if (!this.productHandle) {
      this.variantsFetchSettled = true;
    }

    const variantsMappingScript = this.container.querySelector(`#variants-mapping-${this.blockId}`);
    if (variantsMappingScript) {
      try {
        const variantsArray = JSON.parse(variantsMappingScript.textContent);
        this.buildVariantsMapFromEntries(variantsArray, { fromInlineEmbed: true });
      } catch (e) {
        console.error('Error parsing variants mapping:', e);
      }
    }

    this.updateProductConfigErrorVisibility();

    const cushionDataScript = this.container.querySelector(`#cushion-upsell-data-${this.blockId}`);
    if (cushionDataScript) {
      try {
        this.cushionUpsell = JSON.parse(cushionDataScript.textContent);
      } catch (e) {
        console.error('Error parsing cushion upsell data:', e);
        this.cushionUpsell = null;
      }
    }

    this.setupEventListeners();
    this.setupCushionUpsellUI();
    this.initMiniCart();
    this.updateStepCountForEyelet();
    this.showStep(this.currentStep, { skipScroll: true });
    this.validateDimensionsStep();
    this.validateFullnessStep();
    this.validateHeadingStep();
    this.validateEyeletStep();
    this.validateReviewStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
    this.updateStepIndicator();

    this.loadFullVariantsMapping().then(() => {
      this.updatePrice();
      this.updateSummaryWidthDisplay();
    });
  }

  resolveProductHandle() {
    if (this.productHandle) return this.productHandle;

    const metaScript = this.container.querySelector(`#ccpdp-product-meta-${this.blockId}`);
    if (metaScript) {
      try {
        const meta = JSON.parse(metaScript.textContent);
        if (meta && meta.handle) {
          this.productHandle = meta.handle;
          if (meta.variantsCount && meta.variantsCount > this.expectedVariantsCount) {
            this.expectedVariantsCount = meta.variantsCount;
          }
          if (meta.widthOptionIndex) this.widthOptionIndex = parseInt(meta.widthOptionIndex, 10);
          if (meta.headingOptionIndex) this.headingOptionIndex = parseInt(meta.headingOptionIndex, 10);
          return this.productHandle;
        }
      } catch (e) {
        console.error('Curtain Customizer PDP: error parsing product meta:', e);
      }
    }

    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    if (pathMatch && pathMatch[1]) {
      this.productHandle = decodeURIComponent(pathMatch[1]);
      return this.productHandle;
    }

    if (typeof window.Shopify !== 'undefined') {
      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct && metaProduct.handle) {
        this.productHandle = metaProduct.handle;
        return this.productHandle;
      }
    }

    return '';
  }

  getProductJsonUrls(handle) {
    const encoded = encodeURIComponent(handle);
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const urls = [
      `${root}/products/${encoded}.js`,
      `/products/${encoded}.js`
    ];
    return [...new Set(urls)];
  }

  normalizeCatalogEntries(rawEntries) {
    if (!Array.isArray(rawEntries)) return [];
    return rawEntries
      .map((entry) => {
        if (!entry || !entry.id) return null;
        const width = typeof entry.width === 'number'
          ? entry.width
          : this.parseWidthCmFromOption(entry.width);
        if (!width || width <= 0) return null;
        return {
          width,
          heading: entry.heading || null,
          id: String(entry.id),
          price: entry.price
        };
      })
      .filter(Boolean);
  }

  getWidthSkuCountFromProduct(product) {
    if (!product || !Array.isArray(product.options)) return 0;
    const widthOption = product.options.find((opt) => /^width$/i.test(String(opt.name || '').trim()));
    if (widthOption && Array.isArray(widthOption.values)) {
      return widthOption.values.length;
    }
    return 0;
  }

  getMetafieldCatalogEntries() {
    const script = this.container.querySelector(`#ccpdp-metafield-catalog-${this.blockId}`);
    if (!script) return [];
    try {
      const raw = JSON.parse(script.textContent);
      return this.normalizeCatalogEntries(Array.isArray(raw) ? raw : raw.variants || []);
    } catch (e) {
      console.error('Curtain Customizer PDP: error parsing variant_price_catalog metafield', e);
      return [];
    }
  }

  getStorefrontApiUrl() {
    const shop = window.Shopify?.shop;
    if (!shop) return null;
    return `https://${shop}/api/2024-10/graphql.json`;
  }

  parseStorefrontVariantGid(gid) {
    if (!gid) return '';
    const match = String(gid).match(/ProductVariant\/(\d+)/);
    return match ? match[1] : '';
  }

  mapStorefrontVariantNode(node) {
    if (!node) return null;
    let width = 0;
    let heading = null;
    if (Array.isArray(node.selectedOptions)) {
      node.selectedOptions.forEach((opt) => {
        const name = String(opt.name || '').trim();
        if (/^width$/i.test(name)) {
          width = this.parseWidthCmFromOption(opt.value);
        } else if (/heading/i.test(name)) {
          heading = opt.value;
        }
      });
    }
    const id = this.parseStorefrontVariantGid(node.id);
    const amount = parseFloat(node.price?.amount);
    if (!id || !width || !Number.isFinite(amount)) return null;
    return {
      width,
      heading,
      id,
      price: Math.round(amount * 100)
    };
  }

  async fetchAllVariantsStorefront(handle) {
    const token = this.storefrontAccessToken;
    const apiUrl = this.getStorefrontApiUrl();
    if (!token || !apiUrl) return null;

    const query = `
      query ProductVariants($handle: String!, $cursor: String) {
        product(handle: $handle) {
          variants(first: 250, after: $cursor) {
            pageInfo { hasNextPage endCursor }
            edges {
              node {
                id
                price { amount }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    `;

    let cursor = null;
    const allEntries = [];
    let pageCount = 0;
    const maxPages = 12;

    try {
      while (pageCount < maxPages) {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({
            query,
            variables: { handle, cursor }
          })
        });

        if (!response.ok) {
          console.warn('Curtain Customizer PDP: Storefront API HTTP', response.status);
          break;
        }

        const payload = await response.json();
        if (payload.errors && payload.errors.length) {
          console.warn('Curtain Customizer PDP: Storefront API', payload.errors[0].message);
          break;
        }

        const connection = payload.data?.product?.variants;
        if (!connection) break;

        connection.edges.forEach((edge) => {
          const entry = this.mapStorefrontVariantNode(edge.node);
          if (entry) allEntries.push(entry);
        });

        if (!connection.pageInfo?.hasNextPage) break;
        cursor = connection.pageInfo.endCursor;
        pageCount += 1;
      }
    } catch (error) {
      console.warn('Curtain Customizer PDP: Storefront API fetch failed', error);
      return null;
    }

    if (allEntries.length > 0) {
      return { entries: allEntries, source: 'storefront-graphql' };
    }
    return null;
  }

  isCatalogLikelyIncomplete(entries, widthSkuCount) {
    if (!entries.length) return true;
    const maxWidth = entries.reduce((max, entry) => Math.max(max, entry.width || 0), 0);
    if (widthSkuCount > 84 && entries.length <= 250 && maxWidth < 1500) return true;
    if (this.expectedVariantsCount > 250 && entries.length <= 250 && maxWidth < 1900) return true;
    return false;
  }

  mapProductJsVariantsToEntries(product, widthIdx, headingIdx) {
    if (!product || !Array.isArray(product.variants)) return [];
    return product.variants
      .map((variant) => {
        const options = [variant.option1, variant.option2, variant.option3];
        const width = this.parseWidthCmFromOption(options[widthIdx - 1]);
        const heading = headingIdx ? options[headingIdx - 1] : null;
        return {
          width,
          heading: heading || null,
          id: String(variant.id),
          price: variant.price
        };
      })
      .filter((entry) => entry.width > 0 && entry.id);
  }

  parseWidthCmFromOption(value) {
    if (value == null || value === '') return 0;
    const cleaned = String(value).replace(/cm/gi, '').replace(/\s+/g, '').trim();
    const parsed = parseInt(cleaned, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  getHeadingKeyFromLabel(label) {
    if (!label) return '';
    const normalized = this.normalizeHeadingOptionValue(label);
    if (normalized) return normalized;
    return String(label).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  buildVariantsMapFromEntries(entries, options = {}) {
    if (!Array.isArray(entries) || !entries.length) return;

    const nextMap = { ...this.variantsMap };
    const nextData = { ...this.variantsData };
    const nextHeadingKeys = new Set(this.productHeadingKeys);
    let hasHeadingInEntries = false;
    let maxWidthInBatch = 0;

    entries.forEach((variant) => {
      if (!variant || !variant.width || !variant.id) return;
      if (variant.price == null || variant.price === '') return;

      maxWidthInBatch = Math.max(maxWidthInBatch, variant.width);

      let headingKey = '';
      if (variant.heading) {
        hasHeadingInEntries = true;
        headingKey = this.getHeadingKeyFromLabel(variant.heading);
        if (headingKey && this.isKnownHeadingKey(headingKey)) {
          nextHeadingKeys.add(headingKey);
        }
      }

      const useHeadingKey = Boolean(variant.heading && (this.hasHeadingVariants || hasHeadingInEntries));
      const mapKey = useHeadingKey
        ? `${variant.width}|${headingKey}`
        : String(variant.width);

      nextMap[mapKey] = String(variant.id);
      const priceCents = typeof variant.price === 'number' ? variant.price : parseFloat(variant.price);
      nextData[mapKey] = {
        id: String(variant.id),
        price: priceCents / 100.0
      };
    });

    if (Object.keys(nextMap).length === 0) return;

    this.variantsMap = nextMap;
    this.variantsData = nextData;
    this.productHeadingKeys = nextHeadingKeys;
    if (hasHeadingInEntries) {
      this.hasHeadingVariants = true;
    }

    this.rebuildAvailableWidthsCache();

    const mapKeys = Object.keys(this.variantsMap).length;
    const maxWidthInMap = this.getMaxCatalogWidthCm();
    const entryCount = Math.max(entries.length, mapKeys);

    if (options.fromInlineEmbed) {
      return;
    }

    if (
      !this.productHandle ||
      entryCount >= this.expectedVariantsCount ||
      maxWidthInMap >= 2000 ||
      (this.expectedVariantsCount > 100 && maxWidthInBatch >= 1900)
    ) {
      this.variantsLoaded = true;
    }
  }

  getMaxCatalogWidthCm() {
    let max = 0;
    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (Number.isFinite(width) && width > max) max = width;
    });
    return max;
  }

  rebuildAvailableWidthsCache() {
    const byHeading = {};

    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (!Number.isFinite(width) || width <= 0) return;

      if (pipeIndex >= 0) {
        const headingKey = mapKey.slice(pipeIndex + 1);
        if (!byHeading[headingKey]) byHeading[headingKey] = new Set();
        byHeading[headingKey].add(width);
      } else {
        if (!byHeading._all) byHeading._all = new Set();
        byHeading._all.add(width);
      }
    });

    this.availableWidthsByHeading = {};
    Object.keys(byHeading).forEach((headingKey) => {
      this.availableWidthsByHeading[headingKey] = [...byHeading[headingKey]].sort((a, b) => a - b);
    });
  }

  getAvailableWidthsForHeading(headingKey) {
    if (!this.availableWidthsByHeading || !Object.keys(this.availableWidthsByHeading).length) {
      this.rebuildAvailableWidthsCache();
    }

    if (headingKey && this.availableWidthsByHeading[headingKey]) {
      return this.availableWidthsByHeading[headingKey];
    }

    if (this.availableWidthsByHeading._all && this.availableWidthsByHeading._all.length) {
      return this.availableWidthsByHeading._all;
    }

    const union = new Set();
    Object.values(this.availableWidthsByHeading).forEach((widths) => {
      widths.forEach((width) => union.add(width));
    });
    return [...union].sort((a, b) => a - b);
  }

  /**
   * Width SKUs are 100cm, 110cm, ..., 2000cm. Calculated widths (hooks/fullness) may land
   * between SKUs (e.g. 922cm) â€” pick the smallest catalog width >= target (ceil to SKU).
   */
  snapToProductWidthGridCm(targetCm) {
    const clamped = Math.max(100, Math.min(2000, targetCm));
    if (clamped <= 100) return 100;
    const steps = Math.ceil((clamped - 100) / 10);
    return Math.min(2000, 100 + steps * 10);
  }

  resolveCatalogWidthCm(targetCm, headingKey) {
    const clamped = Math.max(100, Math.min(2000, targetCm));
    const widths = this.getAvailableWidthsForHeading(headingKey);

    if (widths.length) {
      const match = widths.find((width) => width >= clamped);
      if (match != null) return match;
    }

    return this.snapToProductWidthGridCm(clamped);
  }

  getVariantWidthForSelection(calculatedWidthCm, headingKeyOverride = undefined) {
    if (!Number.isFinite(calculatedWidthCm) || calculatedWidthCm <= 0) return 0;

    let headingKey = null;
    if (headingKeyOverride !== undefined) {
      headingKey = headingKeyOverride;
    } else if (this.hasHeadingVariants) {
      headingKey = this.getHeadingVariantKey();
    }

    let roundedCm;
    if (headingKey === 'eyelet') {
      roundedCm = this.roundWidthToNearest50(calculatedWidthCm);
      if (this.isPairPanelMode() && (roundedCm / 2) % 50 !== 0) {
        roundedCm += 50;
      }
    } else {
      roundedCm = this.roundWidthToNearest10(calculatedWidthCm);
    }
    if (roundedCm < 100) roundedCm = 100;
    if (roundedCm > this.maxFabricWidthCm) return 0;

    if (headingKey === 'waterfall') return 0;

    return this.resolveCatalogWidthCm(roundedCm, headingKey) || 0;
  }

  lookupVariantMapKey(variantWidth, headingKey) {
    if (!variantWidth) return null;

    const candidates = [];
    if (headingKey) {
      candidates.push(`${variantWidth}|${headingKey}`);
    }
    if (this.formData.headingStyle) {
      const { name, handle, shopifyHeadingValue } = this.formData.headingStyle;
      [shopifyHeadingValue, name, handle].forEach((label) => {
        const key = this.getHeadingKeyFromLabel(label);
        if (key) candidates.push(`${variantWidth}|${key}`);
      });
    }
    this.shopifyHeadingOptions.forEach((optionValue) => {
      const key = this.getHeadingKeyFromLabel(optionValue);
      if (key) candidates.push(`${variantWidth}|${key}`);
    });
    candidates.push(String(variantWidth));

    for (const key of candidates) {
      if (this.variantsMap[key]) return key;
    }

    const prefix = `${variantWidth}|`;
    return Object.keys(this.variantsMap).find((key) => key.startsWith(prefix)) || null;
  }

  updateProductConfigErrorVisibility() {
    const productError = this.container.querySelector(`#product-config-error-${this.blockId}`);
    if (!productError) return;

    if (Object.keys(this.variantsMap).length > 0 || this.variantId || this.variantPrice > 0) {
      productError.style.display = 'none';
    } else {
      productError.style.display = '';
      console.error('Curtain Customizer PDP: No variants mapping or variant ID found');
    }
  }

  detectOptionIndicesFromProduct(product) {
    const optionNames = Array.isArray(product.options) ? product.options : [];
    let widthIdx = this.widthOptionIndex;
    let headingIdx = this.headingOptionIndex;

    if (!widthIdx) {
      const widthOptionIndex = optionNames.findIndex((name) => /^width$/i.test(String(name || '').trim()));
      if (widthOptionIndex >= 0) widthIdx = widthOptionIndex + 1;
    }

    if (!headingIdx) {
      const headingOptionIndex = optionNames.findIndex((name) => /heading/i.test(String(name || '').trim()));
      if (headingOptionIndex >= 0) headingIdx = headingOptionIndex + 1;
    }

    return { widthIdx, headingIdx };
  }

  async fetchProductJson(handle) {
    const urls = this.getProductJsonUrls(handle);
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
          lastError = new Error(`HTTP ${response.status} for ${url}`);
          continue;
        }
        const product = await response.json();
        if (product && Array.isArray(product.variants) && product.variants.length > 0) {
          return { product, url };
        }
        lastError = new Error(`No variants in response from ${url}`);
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) {
      console.warn('Curtain Customizer PDP: product.js fetch failed', lastError.message);
    }
    return null;
  }

  loadFullVariantsMapping() {
    const handle = this.resolveProductHandle();
    if (!handle) {
      this.variantsFetchSettled = true;
      console.warn('Curtain Customizer PDP: no product handle â€” cannot load variants for pricing');
      return Promise.resolve(false);
    }

    if (this.variantsLoadPromise) {
      return this.variantsLoadPromise;
    }

    this.variantsLoadPromise = (async () => {
        let entries = [];
        let source = '';
        let widthIdx = this.widthOptionIndex;
        let headingIdx = this.headingOptionIndex;
        let widthSkuCount = 0;

        const metafieldEntries = this.getMetafieldCatalogEntries();
        if (metafieldEntries.length) {
          entries = metafieldEntries;
          source = 'variant_price_catalog-metafield';
        }

        if (!entries.length || this.isCatalogLikelyIncomplete(entries, 191)) {
          const storefrontResult = await this.fetchAllVariantsStorefront(handle);
          if (storefrontResult && storefrontResult.entries.length > entries.length) {
            entries = storefrontResult.entries;
            source = storefrontResult.source;
          }
        }

        const productResult = await this.fetchProductJson(handle);
        if (productResult) {
          const { product } = productResult;
          const indices = this.detectOptionIndicesFromProduct(product);
          if (indices.widthIdx) widthIdx = indices.widthIdx;
          if (indices.headingIdx) headingIdx = indices.headingIdx;

          widthSkuCount = this.getWidthSkuCountFromProduct(product);

          if (!entries.length) {
            const jsEntries = this.mapProductJsVariantsToEntries(product, widthIdx, headingIdx);
            if (jsEntries.length) {
              entries = jsEntries;
              source = 'product.js';
            }
          }
        }

        if (!widthIdx) {
          console.warn('Curtain Customizer PDP: could not detect Width option on product');
          return false;
        }

        this.widthOptionIndex = widthIdx;
        if (headingIdx) this.headingOptionIndex = headingIdx;

        if (!entries.length) return false;

        this.buildVariantsMapFromEntries(entries);
        const mapSize = Object.keys(this.variantsMap).length;
        const maxWidth = this.getMaxCatalogWidthCm();
        if (mapSize > 0) {
          console.info(
            `Curtain Customizer PDP: loaded ${mapSize} variant prices (max width ${maxWidth}cm) via ${source || 'unknown'}`
          );
        }
        if (this.isCatalogLikelyIncomplete(entries, widthSkuCount)) {
          console.warn(
            'Curtain Customizer PDP: only',
            entries.length,
            'variants loaded (max',
            maxWidth,
            'cm). Shopify caps Liquid and product.js at 250. Add a Storefront API token in the Curtain Customizer section settings, or populate product metafield custom.variant_price_catalog.'
          );
        }
        return mapSize > 0;
      })()
      .catch((error) => {
        console.error('Curtain Customizer PDP: failed to load product variants', error);
        return false;
      })
      .finally(() => {
        this.variantsFetchSettled = true;
        this.updateProductConfigErrorVisibility();
        this.updatePrice();
        this.updateReviewMeasurementPreview();
      });

    return this.variantsLoadPromise;
  }

  setupEventListeners() {
    // Heading style selection
    const headingStyleOptions = this.container.querySelectorAll('.heading-style-option');
    headingStyleOptions.forEach(option => {
      option.addEventListener('click', () => this.selectHeadingStyle(option));
    });

    // Panel type selection
    const panelTypeOptions = this.container.querySelectorAll('.panel-type-option');
    panelTypeOptions.forEach(option => {
      option.addEventListener('click', () => this.selectPanelType(option));
    });

    // Join type selection (conditional section)
    const joinTypeOptions = this.container.querySelectorAll('.join-type-option');
    joinTypeOptions.forEach(option => {
      option.addEventListener('click', () => this.selectJoinType(option));
    });

    // Fullness selection
    const fullnessOptions = this.container.querySelectorAll('.fullness-option');
    fullnessOptions.forEach(option => {
      option.addEventListener('click', () => this.selectFullness(option));
    });

    // Eyelet color selection
    const eyeletOptions = this.container.querySelectorAll('.eyelet-option');
    eyeletOptions.forEach(option => {
      option.addEventListener('click', () => this.selectEyeletColor(option));
    });

    const waveHooksInput = this.container.querySelector(`#wave-hooks-input-${this.blockId}`);
    if (waveHooksInput) {
      waveHooksInput.addEventListener('input', () => this.onWaveHookCountInput());
    }

    const waveHooksConfirm = this.container.querySelector(`#wave-hooks-confirm-${this.blockId}`);
    if (waveHooksConfirm) {
      waveHooksConfirm.addEventListener('change', (e) => {
        this.formData.waveHooksMismatchAcknowledged = e.target.checked;
        this.validateHeadingStep();
      });
    }

    const waveRailChoices = this.container.querySelectorAll(`[data-wave-rail-choice]`);
    waveRailChoices.forEach((button) => {
      button.addEventListener('click', () => {
        const choice = button.getAttribute('data-wave-rail-choice');
        if (choice === 'yes' || choice === 'no') this.onWaveRailChoice(choice);
      });
    });

    // Dimension inputs
    const widthInput = this.container.querySelector(`#width-input-${this.blockId}`);
    const dropInput = this.container.querySelector(`#drop-input-${this.blockId}`);
    
    // Clear any default values on initialization
    if (widthInput) {
      widthInput.value = '';
      widthInput.addEventListener('input', (e) => {
        const parsed = this.parseDimensionInputValue(e.target.value);
        if (e.target.value !== parsed.displayValue) {
          e.target.value = parsed.displayValue;
        }
        this.formData.width = parsed.value;
        this.setDimensionFieldInvalidState(e.target, parsed.isInvalid);
        
        // Show error if width exceeds 8.00m
        const widthError = this.container.querySelector(`#width-error-${this.blockId}`);
        if (parsed.value > this.maxWidthMeters) {
          if (widthError) widthError.style.display = 'block';
        } else {
          if (widthError) widthError.style.display = 'none';
        }
        
        this.validateDimensionsStep();
        this.updatePrice();
        if (this.isWaveHeading()) {
          this.updateWaveHooksUI();
          this.validateHeadingStep();
        }
        this.updateReviewMeasurementPreview();
      });
    }
    if (dropInput) {
      dropInput.value = '';
      dropInput.addEventListener('input', (e) => {
        const parsed = this.parseDimensionInputValue(e.target.value);
        if (e.target.value !== parsed.displayValue) {
          e.target.value = parsed.displayValue;
        }
        this.formData.drop = parsed.value;
        this.setDimensionFieldInvalidState(e.target, parsed.isInvalid);
        this.updateDropExtraLengthNote();
        this.validateDimensionsStep();
        this.updatePrice();
        this.updateReviewMeasurementPreview();
      });
    }

    // Room name input
    const roomNameInput = this.container.querySelector(`#room-name-input-${this.blockId}`);
    if (roomNameInput) {
      const syncRoomName = (e) => {
        this.formData.roomName = e.target.value.trim();
        roomNameInput.classList.remove('room-name-input__field--attention');
        this.validateReviewStep();
      };
      roomNameInput.addEventListener('input', syncRoomName);
      roomNameInput.addEventListener('change', syncRoomName);
      roomNameInput.addEventListener('blur', syncRoomName);
    }

    // Navigation buttons
    const nextButtons = this.container.querySelectorAll('.curtain-customizer-pdp__next');
    nextButtons.forEach(btn => {
      btn.addEventListener('click', () => this.nextStep());
    });

    const prevButtons = this.container.querySelectorAll('.curtain-customizer-pdp__prev');
    prevButtons.forEach(btn => {
      btn.addEventListener('click', () => this.prevStep());
    });

    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });

    // Add another room button
    const addAnotherBtn = this.container.querySelector('.curtain-customizer-pdp__add-another');
    if (addAnotherBtn) {
      addAnotherBtn.addEventListener('click', () => this.resetForm());
    }

    const restartBtn = this.container.querySelector(`#restart-customizer-btn-${this.blockId}`);
    if (restartBtn) {
      restartBtn.addEventListener('click', () => this.resetForm());
    }
  }

  setupCushionUpsellUI() {
    if (!this.cushionUpsell || !this.cushionUpsell.enabled) return;

    const checkbox = this.container.querySelector(`#cushion-upsell-checkbox-${this.blockId}`);
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        this.formData.addScatterCushions = checkbox.checked;
        this.updatePrice();
      });
    }

    const successBtn = this.container.querySelector(`#success-add-cushions-btn-${this.blockId}`);
    if (successBtn) {
      successBtn.addEventListener('click', () => this.addCushionFromSuccess());
    }

    const tooltipTrigger = this.container.querySelector(`#cushion-tooltip-trigger-${this.blockId}`);
    const tooltipPanel = this.container.querySelector(`#cushion-tooltip-panel-${this.blockId}`);
    const tooltipWrap = tooltipTrigger ? tooltipTrigger.closest('.ccpdp-tooltip') : null;
    if (tooltipTrigger && tooltipPanel && tooltipWrap) {
      this.bindTooltip(tooltipWrap, tooltipTrigger, tooltipPanel);
    }
  }

  bindTooltip(tooltipWrap, tooltipTrigger, tooltipPanel) {
    const toggle = (open) => {
      const isOpen = typeof open === 'boolean' ? open : !tooltipWrap.classList.contains('is-open');
      tooltipWrap.classList.toggle('is-open', isOpen);
      tooltipPanel.hidden = !isOpen;
      tooltipTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };
    tooltipTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });
    document.addEventListener('click', (e) => {
      if (!tooltipWrap.contains(e.target)) toggle(false);
    });
    tooltipTrigger.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') toggle(false);
    });
    return toggle;
  }

  setupTotalWidthTooltip() {
    this.totalWidthTooltipWrap = this.container.querySelector(`#summary-total-width-tooltip-${this.blockId}`);
    this.totalWidthTooltipTrigger = this.container.querySelector(`#summary-total-width-tooltip-trigger-${this.blockId}`);
    this.totalWidthTooltipPanel = this.container.querySelector(`#summary-total-width-tooltip-panel-${this.blockId}`);
    this.totalWidthTooltipText = this.container.querySelector(`#summary-total-width-tooltip-text-${this.blockId}`);

    if (
      this.totalWidthTooltipWrap &&
      this.totalWidthTooltipTrigger &&
      this.totalWidthTooltipPanel
    ) {
      const toggleTotalWidthTooltip = this.bindTooltip(
        this.totalWidthTooltipWrap,
        this.totalWidthTooltipTrigger,
        this.totalWidthTooltipPanel
      );
      this.closeTotalWidthTooltip = () => toggleTotalWidthTooltip(false);
    } else {
      this.closeTotalWidthTooltip = () => {};
    }
  }

  showToast(message) {
    const el = this.container.querySelector(`#ccpdp-toast-${this.blockId}`);
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => {
      el.classList.add('is-visible');
    });
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => {
        el.hidden = true;
      }, 350);
    }, 3800);
  }

  updateSuccessCushionUI() {
    if (!this.cushionUpsell || !this.cushionUpsell.enabled) return;
    const block = this.container.querySelector(`#success-cushion-block-${this.blockId}`);
    const actions = this.container.querySelector(`#success-cushion-actions-${this.blockId}`);
    const already = this.container.querySelector(`#success-cushion-already-${this.blockId}`);

    if (!this.cushionsAddedWithCurtain) {
      if (block) block.style.display = '';
      if (actions) actions.style.display = '';
      if (already) {
        already.textContent = '';
        already.style.display = 'none';
      }
      return;
    }

    // Cushions already in cart (review bundle or success-screen add) â€” hide row; toast only for success-screen add
    if (block) block.style.display = 'none';
  }

  async fetchCartResource() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('Curtain Customizer PDP: failed to fetch cart resource', error);
      return null;
    }
  }

  queueCartLineAttributeSync(cart) {
    const sync = window.CurtainryPdp && window.CurtainryPdp.syncCartLineAttributes;
    if (typeof sync !== 'function' || !cart) return;
    void sync(cart).catch((error) => {
      console.warn('Curtain Customizer PDP: failed to sync cart line attributes', error);
    });
  }

  async notifyCartUpdated(options = {}) {
    const cart = options.cart || await this.fetchCartResource();
    const itemCount = Number.isFinite(options.itemCount)
      ? options.itemCount
      : (cart && Number.isFinite(cart.item_count) ? cart.item_count : undefined);
    const detail = {
      resource: cart || null,
      sourceId: `curtain-customizer-pdp-${this.blockId}`,
      data: {
        source: 'curtain-customizer-pdp',
        itemCount
      }
    };

    // Theme-native cart event consumed by product/cart components.
    document.dispatchEvent(new CustomEvent('cart:update', { detail }));
    // Backward-compatible event for existing custom listeners.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  }

  initMiniCart() {
    this.miniCartEl = this.container.querySelector(`#ccpdp-mini-cart-${this.blockId}`);
    this.miniCartToggle = this.container.querySelector(`#ccpdp-mini-cart-toggle-${this.blockId}`);
    this.miniCartPanel = this.container.querySelector(`#ccpdp-mini-cart-panel-${this.blockId}`);
    this.miniCartItemsList = this.container.querySelector(`#ccpdp-mini-cart-items-${this.blockId}`);
    this.miniCartSummaryEl = this.container.querySelector(`#ccpdp-mini-cart-summary-${this.blockId}`);
    this.miniCartCountEl = this.container.querySelector(`#ccpdp-mini-cart-count-${this.blockId}`);
    this.miniCartTotalEl = this.container.querySelector(`#ccpdp-mini-cart-total-${this.blockId}`);
    this.miniCartExpanded = false;

    if (!this.miniCartEl || !this.miniCartToggle) return;

    this.miniCartToggle.addEventListener('click', () => {
      this.setMiniCartExpanded(!this.miniCartExpanded);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.miniCartExpanded) return;
      if (!this.miniCartEl || this.miniCartEl.hidden) return;
      this.setMiniCartExpanded(false);
    });
  }

  setMiniCartExpanded(expanded) {
    if (!this.miniCartEl || !this.miniCartToggle || !this.miniCartPanel) return;

    this.miniCartExpanded = !!expanded;
    this.miniCartEl.classList.toggle('is-open', this.miniCartExpanded);
    this.miniCartToggle.setAttribute('aria-expanded', this.miniCartExpanded ? 'true' : 'false');
    this.miniCartPanel.hidden = !this.miniCartExpanded;
  }

  resetMiniCart() {
    if (!this.miniCartEl) return;

    this.setMiniCartExpanded(false);
    this.miniCartEl.hidden = true;
    if (this.miniCartCountEl) this.miniCartCountEl.textContent = '';
    if (this.miniCartTotalEl) this.miniCartTotalEl.textContent = '';
    if (this.miniCartItemsList) this.miniCartItemsList.innerHTML = '';
  }

  formatCartMoney(cents) {
    const amount = typeof cents === 'number' && Number.isFinite(cents) ? cents / 100 : 0;
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: this.shopCurrency || 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  getPropertyValue(properties, key) {
    if (!properties || typeof properties !== 'object') return '';
    const value = properties[key];
    return value == null ? '' : String(value).trim();
  }

  getVisibleProperties(properties) {
    if (!properties || typeof properties !== 'object') return [];

    return Object.entries(properties)
      .filter(([key, value]) => {
        if (!value && value !== 0) return false;
        if (String(value).trim() === '') return false;
        return !String(key).startsWith('_');
      })
      .map(([key, value]) => ({ key, value: String(value).trim() }));
  }

  parseLengthToMeters(value) {
    if (!value) return null;
    const normalized = String(value).replace(/,/g, '.').trim();
    const metersMatch = normalized.match(/([\d.]+)\s*m\b/i);
    if (metersMatch) {
      const meters = parseFloat(metersMatch[1]);
      return Number.isFinite(meters) ? meters : null;
    }
    const centimetersMatch = normalized.match(/([\d.]+)\s*cm\b/i);
    if (centimetersMatch) {
      const centimeters = parseFloat(centimetersMatch[1]);
      return Number.isFinite(centimeters) ? centimeters / 100 : null;
    }
    return null;
  }

  formatLengthInMeters(value) {
    const meters = this.parseLengthToMeters(value);
    if (meters == null) return '';
    return `${this.formatMetersValue(meters)} m`;
  }

  normalizeUnitTextToMeters(text) {
    if (!text) return '';
    return String(text).replace(/(\d+(?:\.\d+)?)\s*cm\b/gi, (_, amount) => {
      const meters = parseFloat(amount);
      if (!Number.isFinite(meters)) return `${amount}cm`;
      return `${this.formatMetersValue(meters / 100)} m`;
    });
  }

  formatPanelLabel(panelsValue) {
    const panels = String(panelsValue || '').toLowerCase();
    if (panels.includes('pair')) return 'Pair';
    if (panels.includes('single')) return 'Single';
    return panelsValue || '';
  }

  formatMiniCartItemSubtitle(item) {
    const properties = item.properties || {};
    const heading = this.getPropertyValue(properties, '_Heading Style');
    const panels = this.formatPanelLabel(this.getPropertyValue(properties, 'Panels'));
    const fabricWidthM = this.parseLengthToMeters(this.getPropertyValue(properties, 'Fabric Width'));
    const dropM = this.parseLengthToMeters(this.getPropertyValue(properties, 'Curtain Drop'));

    const parts = [];
    if (heading) parts.push(heading);
    if (panels) parts.push(panels);
    if (fabricWidthM != null && dropM != null) {
      parts.push(`${this.formatMetersValue(fabricWidthM)} m x ${this.formatMetersValue(dropM)} m`);
    }

    if (parts.length > 0) {
      return {
        subtitle: parts.join(' · '),
        room: this.getPropertyValue(properties, 'Room')
      };
    }

    const curtainSize = this.getPropertyValue(properties, 'Curtain Size');
    const headingStyle = this.getPropertyValue(properties, 'Heading Style');
    if (curtainSize || headingStyle) {
      const standardParts = [];
      const formattedSize = this.formatLengthInMeters(curtainSize);
      if (formattedSize) standardParts.push(formattedSize);
      if (headingStyle) standardParts.push(headingStyle);
      return {
        subtitle: standardParts.join(' · '),
        room: ''
      };
    }

    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : '';
    if (variantTitle) {
      return { subtitle: this.normalizeUnitTextToMeters(variantTitle), room: '' };
    }

    const visibleProps = this.getVisibleProperties(properties);
    return {
      subtitle: this.normalizeUnitTextToMeters(
        visibleProps.map((prop) => prop.value).join(' · ')
      ),
      room: ''
    };
  }

  renderMiniCartItem(item) {
    const li = document.createElement('li');
    li.className = 'ccpdp-mini-cart__item';

    const details = document.createElement('div');
    details.className = 'ccpdp-mini-cart__item-details';

    const title = document.createElement('p');
    title.className = 'ccpdp-mini-cart__item-title';
    title.textContent = item.product_title || item.title || 'Item';

    const { subtitle, room } = this.formatMiniCartItemSubtitle(item);
    details.appendChild(title);

    if (subtitle) {
      const props = document.createElement('p');
      props.className = 'ccpdp-mini-cart__item-props';
      props.textContent = subtitle;
      details.appendChild(props);
    }

    if (room) {
      const roomEl = document.createElement('p');
      roomEl.className = 'ccpdp-mini-cart__item-room';
      roomEl.textContent = room;
      details.appendChild(roomEl);
    }

    const price = document.createElement('p');
    price.className = 'ccpdp-mini-cart__item-price';
    price.textContent = this.formatCartMoney(item.final_line_price);

    li.appendChild(details);
    li.appendChild(price);
    return li;
  }

  renderMiniCart(cart) {
    if (!this.miniCartEl || !cart || !Array.isArray(cart.items)) return;

    const itemCount = Number.isFinite(cart.item_count) ? cart.item_count : cart.items.length;

    if (this.miniCartCountEl) this.miniCartCountEl.textContent = String(itemCount);
    if (this.miniCartToggle) {
      this.miniCartToggle.setAttribute(
        'aria-label',
        `Your order so far, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}, ${this.formatCartMoney(cart.total_price)}`
      );
    }
    if (this.miniCartTotalEl) this.miniCartTotalEl.textContent = this.formatCartMoney(cart.total_price);

    if (this.miniCartItemsList) {
      this.miniCartItemsList.innerHTML = '';
      cart.items.forEach((item) => {
        this.miniCartItemsList.appendChild(this.renderMiniCartItem(item));
      });
    }

    this.miniCartEl.hidden = itemCount === 0;
    this.setMiniCartExpanded(false);
  }

  async refreshMiniCart(cart) {
    if (!this.miniCartEl) return;

    const resolvedCart = cart || await this.fetchCartResource();
    if (!resolvedCart) {
      this.resetMiniCart();
      return;
    }

    this.renderMiniCart(resolvedCart);
  }

  async addCushionFromSuccess() {
    if (!this.cushionUpsell || !this.cushionUpsell.variantId || this.isAddingCushionFromSuccess) return;
    this.isAddingCushionFromSuccess = true;
    const btn = this.container.querySelector(`#success-add-cushions-btn-${this.blockId}`);
    if (btn) btn.disabled = true;
    try {
      const vid = String(this.cushionUpsell.variantId).trim();
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: vid,
            quantity: 1
          }]
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Could not add cushions to cart');
      }
      // Toast only when cushions were not already added with the curtain on the review step
      const showCushionAddedToast = !this.cushionsAddedWithCurtain;
      this.cushionsAddedWithCurtain = true;
      this.updateSuccessCushionUI();
      const cart = await this.fetchCartResource();
      await this.notifyCartUpdated({ cart, itemCount: cart?.item_count });
      await this.refreshMiniCart(cart);
      this.queueCartLineAttributeSync(cart);
      if (showCushionAddedToast) {
        this.showToast(this.cushionUpsell.toastMessage || 'Matching cushions added to your cart.');
      }
    } catch (err) {
      console.error(err);
      this.showError(err.message || 'Could not add cushions.');
    } finally {
      this.isAddingCushionFromSuccess = false;
      if (btn) btn.disabled = false;
    }
  }

  selectHeadingStyle(option) {
    const selectedName = (option.dataset.headingStyleName || '').toLowerCase();
    const isWaveSelection = selectedName.includes('wave');

    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.heading-style-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.headingStyle = {
      id: option.dataset.headingStyleId,
      name: option.dataset.headingStyleName,
      handle: option.dataset.headingStyleHandle || null,
      shopifyHeadingValue: option.dataset.shopifyHeadingValue || null
    };

    // Update hidden inputs
    const headingStyleIdInput = this.container.querySelector(`#heading-style-id-${this.blockId}`);
    const headingStyleNameInput = this.container.querySelector(`#heading-style-name-${this.blockId}`);
    if (headingStyleIdInput) headingStyleIdInput.value = this.formData.headingStyle.id;
    if (headingStyleNameInput) headingStyleNameInput.value = this.formData.headingStyle.name;

    // Check if eyelet is selected and update step count
    this.updateStepCountForEyelet();
    
    // Apply special constraints for Waterfall heading style
    this.applyWaterfallConstraints();
    
    // Update join type section visibility based on heading style
    this.updateJoinTypeSectionVisibility();

    // Update fullness options based on heading style (e.g. hide 2X for wave)
    this.updateFullnessOptionsForHeadingStyle();
    this.updateHeadingStyleNotice();
    this.updateWaveHooksSectionVisibility();
    
    // Keep current step in bounds if eyelet step gets removed
    if (this.currentStep > this.totalSteps) {
      this.currentStep = this.totalSteps;
      this.showStep(this.currentStep);
    }

    this.validateHeadingStep();
    this.validateFullnessWidth();
    this.validateFullnessStep();
    this.updateStepIndicator();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  checkIfEyeletSelected() {
    if (!this.formData.headingStyle || !this.formData.headingStyle.name) return false;
    return this.formData.headingStyle.name.toLowerCase().includes('eyelet');
  }

  updateStepCountForEyelet() {
    const isEyelet = this.checkIfEyeletSelected();
    this.totalSteps = isEyelet ? 5 : 4;
    
    // Update step indicator labels
    const step2Label = this.container.querySelector(`#step-2-label-${this.blockId}`);
    const step3Label = this.container.querySelector(`#step-3-label-${this.blockId}`);
    const step4Label = this.container.querySelector(`#step-4-label-${this.blockId}`);
    const step5Indicator = this.container.querySelector(`#step-indicator-5-${this.blockId}`);
    
    // Update dimensions and fullness nav copy
    const dimensionsNextBtn = this.container.querySelector(`#dimensions-next-btn-${this.blockId}`);
    const fullnessNextBtn = this.container.querySelector(`#fullness-next-btn-${this.blockId}`);
    const headingNextBtn = this.container.querySelector(`#heading-next-btn-${this.blockId}`);
    const reviewPrevBtn = this.container.querySelector(`#review-prev-btn-${this.blockId}`);
    
    if (isEyelet) {
      if (step2Label) step2Label.textContent = 'Fullness';
      if (step3Label) step3Label.textContent = 'Heading Style';
      if (step4Label) step4Label.textContent = 'Eyelet Color';
      if (step5Indicator) step5Indicator.style.display = 'flex';
      if (dimensionsNextBtn) dimensionsNextBtn.textContent = 'Next: Fullness';
      if (fullnessNextBtn) fullnessNextBtn.textContent = 'Next: Heading Style';
      if (headingNextBtn) headingNextBtn.textContent = 'Next: Eyelet Color';
      if (reviewPrevBtn) reviewPrevBtn.textContent = 'Change Eyelet Color';
    } else {
      if (step2Label) step2Label.textContent = 'Fullness';
      if (step3Label) step3Label.textContent = 'Heading Style';
      if (step4Label) step4Label.textContent = 'Review';
      if (step5Indicator) step5Indicator.style.display = 'none';
      if (dimensionsNextBtn) dimensionsNextBtn.textContent = 'Next: Fullness';
      if (fullnessNextBtn) fullnessNextBtn.textContent = 'Next: Heading Style';
      if (headingNextBtn) headingNextBtn.textContent = 'Next: Review';
      if (reviewPrevBtn) reviewPrevBtn.textContent = 'Change Heading Style';
      // Clear eyelet selection if not eyelet heading style
      this.formData.eyeletColor = null;
      const eyeletOptions = this.container.querySelectorAll('.eyelet-option');
      eyeletOptions.forEach(opt => opt.classList.remove('selected'));
    }

    const stepIndicator = this.container.querySelector('.step-indicator');
    if (stepIndicator) {
      stepIndicator.classList.toggle('step-indicator--count-5', isEyelet);
    }
  }

  selectEyeletColor(option) {
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.eyelet-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.eyeletColor = {
      id: option.dataset.eyeletId,
      name: option.dataset.eyeletName
    };

    // Update hidden inputs
    const eyeletIdInput = this.container.querySelector(`#eyelet-id-${this.blockId}`);
    const eyeletNameInput = this.container.querySelector(`#eyelet-name-${this.blockId}`);
    if (eyeletIdInput) eyeletIdInput.value = this.formData.eyeletColor.id;
    if (eyeletNameInput) eyeletNameInput.value = this.formData.eyeletColor.name;

    this.validateEyeletStep();
  }

  selectPanelType(option) {
    // Ignore clicks on disabled panel type options
    if (option.classList.contains('panel-type-option--disabled')) {
      return;
    }
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.panel-type-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.panelType = option.dataset.panelType;

    // Update join type section visibility based on panel type
    this.updateJoinTypeSectionVisibility();

    this.validateDimensionsStep();
    this.validateFullnessWidth();
    this.validateFullnessStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  selectFullness(option) {
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.fullness-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.fullness = {
      id: option.dataset.fullnessId,
      name: option.dataset.fullnessName,
      value: parseFloat(option.dataset.fullnessValue)
    };

    // Update hidden inputs
    const fullnessIdInput = this.container.querySelector(`#fullness-id-${this.blockId}`);
    const fullnessNameInput = this.container.querySelector(`#fullness-name-${this.blockId}`);
    const fullnessValueInput = this.container.querySelector(`#fullness-value-${this.blockId}`);
    if (fullnessIdInput) fullnessIdInput.value = this.formData.fullness.id;
    if (fullnessNameInput) fullnessNameInput.value = this.formData.fullness.name;
    if (fullnessValueInput) fullnessValueInput.value = this.formData.fullness.value;

    // Update price
    this.updatePrice();

    // Validate width x fullness doesn't exceed 2000cm
    this.validateFullnessWidth();

    this.validateFullnessStep();
    this.updateFullnessDescription(option);
    if (this.isWaveHeading()) {
      this.updateWaveHooksUI();
      this.validateHeadingStep();
    }
    this.updateReviewMeasurementPreview();
  }

  updateFullnessDescription(option) {
    const panel = this.container.querySelector(`#fullness-description-panel-${this.blockId}`);
    const textEl = this.container.querySelector(`#fullness-description-text-${this.blockId}`);
    if (!panel || !textEl) return;

    const info = option && option.dataset ? (option.dataset.fullnessInfo || '').trim() : '';
    if (!info) {
      panel.hidden = true;
      textEl.textContent = '';
      return;
    }

    textEl.textContent = info;
    panel.hidden = false;
  }

  selectJoinType(option) {
    // Ignore clicks on disabled join type options
    if (option.classList.contains('join-type-option--disabled')) {
      return;
    }
    // Remove previous selection
    const allOptions = this.container.querySelectorAll('.join-type-option');
    allOptions.forEach(opt => opt.classList.remove('selected'));

    // Add selection
    option.classList.add('selected');

    // Store data
    this.formData.joinType = option.dataset.joinType;

    // Update hidden input
    const joinTypeInput = this.container.querySelector(`#join-type-${this.blockId}`);
    if (joinTypeInput) joinTypeInput.value = this.formData.joinType;

    this.validateDimensionsStep();
    this.updateReviewMeasurementPreview();
  }

  shouldShowJoinTypeSection() {
    // Only relevant when we have heading style and panel type
    if (!this.formData.headingStyle || !this.formData.headingStyle.name || !this.formData.panelType) return false;

    const name = this.formData.headingStyle.name.toLowerCase();
    const isTape = name.includes('double tape') || name.includes('single tape');
    const isWaterfall = name.includes('waterfall');
    const isPair = this.formData.panelType === 'pair';

    // Tape styles only show join section when pair is selected,
    // Waterfall always shows join section (panel type is forced to pair)
    return (isTape && isPair) || isWaterfall;
  }

  updateJoinTypeSectionVisibility() {
    const section = this.container.querySelector(`#join-type-section-${this.blockId}`);
    if (!section) return; // Not rendered for non-curtain form types

    const shouldShow = this.shouldShowJoinTypeSection();

    if (shouldShow) {
      section.style.display = '';

      // Default to separate if nothing chosen yet
      if (!this.formData.joinType) {
        const defaultOption = section.querySelector('.join-type-option[data-join-type="separate"]');
        if (defaultOption) {
          this.selectJoinType(defaultOption);
        }
      }
    } else {
      section.style.display = 'none';
      this.formData.joinType = null;
      section.querySelectorAll('.join-type-option').forEach(opt => opt.classList.remove('selected'));
      const joinTypeInput = this.container.querySelector(`#join-type-${this.blockId}`);
      if (joinTypeInput) joinTypeInput.value = '';
    }
  }

  isWaterfallHeading() {
    if (!this.formData.headingStyle || !this.formData.headingStyle.name) return false;
    return this.formData.headingStyle.name.toLowerCase().includes('waterfall');
  }

  isPairPanelMode() {
    return this.isWaterfallHeading() || this.formData.panelType === 'pair';
  }

  isExtraLength() {
    const drop = parseFloat(this.formData.drop);
    return Number.isFinite(drop) && drop > this.standardMaxDropMeters;
  }

  getFullnessNumericValue(fallbackFullness) {
    if (this.formData.fullness) {
      const value = parseFloat(this.formData.fullness.value);
      if (Number.isFinite(value) && value > 0) return value;
    }
    if (Number.isFinite(fallbackFullness) && fallbackFullness > 0) return fallbackFullness;
    return null;
  }

  getWaveStandardFabricWidthMeters() {
    const widthMeters = parseFloat(this.formData.width);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0) return 0;
    return widthMeters * this.waveForcedFullness;
  }

  isWaveOrderingCurtainryRail() {
    return this.isWaveHeading() && this.formData.waveRailChoice === 'no';
  }

  getWaveHooksSummaryText() {
    if (this.isWaveOrderingCurtainryRail()) return 'TBD';
    if (this.formData.trackHookCount != null) return String(this.formData.trackHookCount);
    return '-';
  }

  getBaseFabricWidthMeters(options = {}) {
    if (this.isWaveHeading()) {
      if (this.isWaveOrderingCurtainryRail()) {
        return this.getWaveStandardFabricWidthMeters();
      }
      if (!this.isWaveHookCountValid(this.formData.trackHookCount)) return 0;
      const hookCm = this.getWaveWidthFromHooksCm(this.formData.trackHookCount);
      return hookCm > 0 ? hookCm / 100 : 0;
    }

    const widthMeters = parseFloat(this.formData.width);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0) return 0;
    const fullnessValue = this.getFullnessNumericValue(options.fallbackFullness);
    if (!Number.isFinite(fullnessValue) || fullnessValue <= 0) return 0;
    return widthMeters * fullnessValue;
  }

  getExtraLengthFabricMetres(options = {}) {
    const drop = parseFloat(this.formData.drop);
    if (!Number.isFinite(drop) || drop <= 0) return 0;
    const baseWidthM = this.getBaseFabricWidthMeters(options);
    if (!Number.isFinite(baseWidthM) || baseWidthM <= 0) return 0;

    const hem = this.extraLengthHemMeters;
    const roll = this.fabricRollWidthMeters;
    const tolerance = this.extraLengthWidthToleranceMeters;
    const panelWidthM = this.isPairPanelMode() ? baseWidthM / 2 : baseWidthM;
    const widthForRolls = panelWidthM - tolerance;
    if (!Number.isFinite(widthForRolls) || widthForRolls <= 0) return 0;

    const fabricMetres = this.ceilRollCount(widthForRolls, roll) * (drop + hem);
    return this.isPairPanelMode() ? fabricMetres * 2 : fabricMetres;
  }

  ceilRollCount(widthForRolls, rollWidth) {
    if (!Number.isFinite(widthForRolls) || !Number.isFinite(rollWidth) || widthForRolls <= 0 || rollWidth <= 0) {
      return 0;
    }
    const ratio = Number((widthForRolls / rollWidth).toFixed(10));
    return Math.max(1, Math.ceil(ratio));
  }

  getFinishedWidthMeters(options = {}) {
    return this.getBaseFabricWidthMeters(options);
  }

  getReviewFinishedWidthMeters(options = {}) {
    const finishedM = this.getFinishedWidthMeters(options);
    if (!finishedM) return 0;
    if (this.getHeadingVariantKey() === 'eyelet') {
      const snappedCm = this.getVariantWidthForSelection(finishedM * 100, 'eyelet');
      return snappedCm > 0 ? snappedCm / 100 : finishedM;
    }
    return finishedM;
  }

  getReviewFinishedPanelWidthMeters(options = {}) {
    const total = this.getReviewFinishedWidthMeters(options);
    if (!total) return 0;
    return this.isPairPanelMode() ? total / 2 : total;
  }

  getReviewDiagramTotalWidthMeters() {
    if (this.isExtraLength()) {
      return this.getReviewFinishedWidthMeters();
    }

    const estimatedDisplayWidthCm = this.getDisplayFullnessWidthCm();
    const pricedWidthCm = this.isWaveHeading()
      ? this.getVariantWidthForSelection(this.getEffectiveWidthCm())
      : this.getVariantWidthForSelection(this.getEstimatedWidthCm());
    const shouldUsePricedWidth = this.isWaveHeading() || this.getHeadingVariantKey() === 'eyelet';
    const previewWidthCm = shouldUsePricedWidth && pricedWidthCm > 0
      ? pricedWidthCm
      : estimatedDisplayWidthCm;
    return previewWidthCm > 0 ? previewWidthCm / 100 : 0;
  }

  getReviewDiagramPanelWidthMeters() {
    const total = this.getReviewDiagramTotalWidthMeters();
    if (!total) return 0;
    return this.isPairPanelMode() ? total / 2 : total;
  }

  getPricedWidthCm(options = {}) {
    if (this.isExtraLength()) {
      if (this.isWaveHeading() && !this.isWaveOrderingCurtainryRail() && !this.isWaveHookCountValid(this.formData.trackHookCount)) {
        return 0;
      }
      const fabricMetres = this.getExtraLengthFabricMetres(options);
      return fabricMetres > 0 ? fabricMetres * 100 : 0;
    }

    if (this.isWaveHeading()) {
      return this.getEffectiveWidthCm();
    }

    const widthMeters = parseFloat(this.formData.width);
    const fullness = this.getFullnessNumericValue(options.fallbackFullness);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0 || !Number.isFinite(fullness) || fullness <= 0) {
      return 0;
    }
    return widthMeters * 100 * fullness;
  }

  exceedsMaxPricedWidth(widthCm, headingKey) {
    if (!Number.isFinite(widthCm) || widthCm <= 0) return false;
    if (headingKey === 'waterfall') return false;
    if (widthCm > this.maxFabricWidthCm) return true;
    const snapped = this.getVariantWidthForSelection(widthCm, headingKey);
    return !snapped || snapped > this.maxFabricWidthCm;
  }

  updateDropExtraLengthNote() {
    const dropError = this.container.querySelector(`#drop-error-${this.blockId}`);
    const extraNote = this.container.querySelector(`#drop-extra-length-note-${this.blockId}`);
    if (dropError) dropError.style.display = 'none';
    if (extraNote) extraNote.style.display = this.isExtraLength() ? 'inline-block' : 'none';
  }

  isWaveHeading() {
    const headingStyle = this.formData.headingStyle;
    if (!headingStyle) return false;
    return (
      (headingStyle.handle && headingStyle.handle.toLowerCase() === 'wave') ||
      (!headingStyle.handle && headingStyle.name && headingStyle.name.toLowerCase().includes('wave'))
    );
  }

  getWaveWidthFromHooksCm(hooks) {
    const count = parseInt(hooks, 10);
    if (!Number.isFinite(count) || count < 2) return 0;
    return (count - 1) * 12 + 10;
  }

  getEstimatedWidthCm() {
    return this.getTotalWidthWithFullnessCm();
  }

  /** Track width x fullness, rounded to 10cm â€” for display on review (not variant SKU). */
  getDisplayFullnessWidthCm() {
    const estimatedCm = this.getEstimatedWidthCm();
    if (!estimatedCm || estimatedCm <= 0) return 0;
    return this.roundWidthToNearest10(estimatedCm);
  }

  isWaveHookCountValid(hooks) {
    const count = parseInt(hooks, 10);
    if (!Number.isFinite(count) || count < 2 || count % 2 !== 0) return false;
    const hookCm = this.getWaveWidthFromHooksCm(count);
    if (!hookCm) return false;
    const variantWidth = this.isWaveHeading()
      ? this.getVariantWidthForSelection(hookCm)
      : this.roundWidthToNearest10(hookCm);
    return variantWidth >= 100 && variantWidth <= 2000;
  }

  isWaveHooksMismatch() {
    if (!this.isWaveHeading()) return false;
    const hooks = this.formData.trackHookCount;
    if (!this.isWaveHookCountValid(hooks)) return false;

    const hookCm = this.getWaveWidthFromHooksCm(hooks);
    const expectedRange = this.getWaveExpectedWidthRangeCm();
    if (!expectedRange || !hookCm) return false;

    const tolerance = this.waveWidthMismatchTolerance;
    const minCm = expectedRange.lowerBoundCm * (1 - tolerance);
    const maxCm = expectedRange.upperBoundCm * (1 + tolerance);
    return hookCm < minCm || hookCm > maxCm;
  }

  getEffectiveWidthCm() {
    if (this.isWaveOrderingCurtainryRail()) {
      return this.getWaveStandardFabricWidthMeters() * 100;
    }
    if (this.isWaveHeading() && this.isWaveHookCountValid(this.formData.trackHookCount)) {
      return this.getWaveWidthFromHooksCm(this.formData.trackHookCount);
    }
    return this.getEstimatedWidthCm();
  }

  clearHookEntryFields() {
    this.formData.trackHookCount = null;
    this.formData.waveHooksMismatchAcknowledged = false;

    const hookInput = this.container.querySelector(`#wave-hooks-input-${this.blockId}`);
    if (hookInput) hookInput.value = '';

    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.blockId}`);
    if (confirmCheckbox) confirmCheckbox.checked = false;

    const hiddenInput = this.container.querySelector(`#track-hook-count-${this.blockId}`);
    if (hiddenInput) hiddenInput.value = '';

    const calculatedEl = this.container.querySelector(`#wave-hooks-calculated-${this.blockId}`);
    if (calculatedEl) calculatedEl.textContent = '';

    const errorEl = this.container.querySelector(`#wave-hooks-error-${this.blockId}`);
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }

    const mismatchEl = this.container.querySelector(`#wave-hooks-mismatch-${this.blockId}`);
    if (mismatchEl) mismatchEl.style.display = 'none';

    const mismatchText = this.container.querySelector(`#wave-hooks-mismatch-text-${this.blockId}`);
    if (mismatchText) mismatchText.textContent = '';
  }

  clearWaveHooks() {
    this.formData.waveRailChoice = null;
    this.formData.orderingCurtainryWaveRail = false;
    this.clearHookEntryFields();
    this.updateWaveRailChoiceUi();
  }

  updateWaveHooksSectionVisibility() {
    const section = this.container.querySelector(`#wave-hooks-section-${this.blockId}`);
    if (!section) return;

    if (this.isWaveHeading()) {
      section.style.display = '';
      this.updateWaveHooksUI();
    } else {
      section.style.display = 'none';
      this.clearWaveHooks();
    }
  }

  syncOrderingCurtainryWaveRail() {
    this.formData.orderingCurtainryWaveRail = this.formData.waveRailChoice === 'no';
  }

  updateWaveRailChoiceUi() {
    const choice = this.formData.waveRailChoice;
    const yesBtn = this.container.querySelector(`#wave-rail-choice-yes-${this.blockId}`);
    const noBtn = this.container.querySelector(`#wave-rail-choice-no-${this.blockId}`);
    const entry = this.container.querySelector(`#wave-hooks-entry-${this.blockId}`);
    const disclaimer = this.container.querySelector(`#wave-no-rail-disclaimer-${this.blockId}`);

    if (yesBtn) {
      const selected = choice === 'yes';
      yesBtn.classList.toggle('is-selected', selected);
      yesBtn.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    if (noBtn) {
      const selected = choice === 'no';
      noBtn.classList.toggle('is-selected', selected);
      noBtn.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    if (entry) entry.hidden = choice !== 'yes';
    if (disclaimer) disclaimer.hidden = choice !== 'no';
  }

  onWaveRailChoice(choice) {
    const previous = this.formData.waveRailChoice;
    this.formData.waveRailChoice = choice;
    this.syncOrderingCurtainryWaveRail();

    if (choice === 'no' && previous !== 'no') {
      this.clearHookEntryFields();
    }

    this.updateWaveRailChoiceUi();
    this.updateWaveHooksUI();
    this.validateHeadingStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  updateWaveHooksUI() {
    this.updateWaveRailChoiceUi();
    if (this.formData.waveRailChoice !== 'yes') {
      if (this.formData.waveRailChoice === 'no') {
        this.formData.trackHookCount = null;
        this.formData.waveHooksMismatchAcknowledged = false;
      }
      return;
    }

    const hookInput = this.container.querySelector(`#wave-hooks-input-${this.blockId}`);
    const calculatedEl = this.container.querySelector(`#wave-hooks-calculated-${this.blockId}`);
    const errorEl = this.container.querySelector(`#wave-hooks-error-${this.blockId}`);
    const mismatchEl = this.container.querySelector(`#wave-hooks-mismatch-${this.blockId}`);
    const mismatchText = this.container.querySelector(`#wave-hooks-mismatch-text-${this.blockId}`);
    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.blockId}`);
    const hiddenInput = this.container.querySelector(`#track-hook-count-${this.blockId}`);

    const rawValue = hookInput ? this.sanitizeWaveHookCountInput(hookInput) : '';
    const parsed = rawValue === '' ? null : parseInt(rawValue, 10);

    if (rawValue === '' || !Number.isFinite(parsed)) {
      this.formData.trackHookCount = null;
      if (hiddenInput) hiddenInput.value = '';
      if (calculatedEl) calculatedEl.textContent = '';
      if (errorEl) {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }
      if (mismatchEl) mismatchEl.style.display = 'none';
      this.formData.waveHooksMismatchAcknowledged = false;
      if (confirmCheckbox) confirmCheckbox.checked = false;
      return;
    }

    this.formData.trackHookCount = parsed;
    if (hiddenInput) hiddenInput.value = String(parsed);

    const isValid = this.isWaveHookCountValid(parsed);
    if (calculatedEl) calculatedEl.textContent = '';

    if (errorEl) {
      if (parsed < 2) {
        errorEl.textContent = 'Enter at least 2 hooks.';
        errorEl.style.display = 'block';
      } else if (parsed % 2 !== 0) {
        errorEl.textContent = this.waveHooksEvenError;
        errorEl.style.display = 'block';
      } else if (!isValid) {
        errorEl.textContent = this.waveHooksRangeError;
        errorEl.style.display = 'block';
      } else {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }
    }

    const hasMismatch = isValid && this.isWaveHooksMismatch();
    if (mismatchEl && mismatchText) {
      if (hasMismatch) {
        mismatchText.textContent = this.waveHooksMismatchWarning;
        mismatchEl.style.display = 'block';
      } else {
        mismatchEl.style.display = 'none';
        mismatchText.textContent = '';
        this.formData.waveHooksMismatchAcknowledged = false;
        if (confirmCheckbox) confirmCheckbox.checked = false;
      }
    }

    if (!hasMismatch) {
      this.formData.waveHooksMismatchAcknowledged = false;
    }
  }

  sanitizeWaveHookCountInput(hookInput) {
    const current = String(hookInput.value || '');
    const sanitized = (current.match(/^\d+/) || [''])[0];
    if (current !== sanitized) {
      hookInput.value = sanitized;
    }
    return sanitized;
  }

  onWaveHookCountInput() {
    this.formData.waveHooksMismatchAcknowledged = false;
    const confirmCheckbox = this.container.querySelector(`#wave-hooks-confirm-${this.blockId}`);
    if (confirmCheckbox) confirmCheckbox.checked = false;
    this.updateWaveHooksUI();
    this.validateHeadingStep();
    this.updatePrice();
    this.updateReviewMeasurementPreview();
  }

  isHeadingStepValid() {
    if (!this.formData.headingStyle) return false;
    if (!this.isWaveHeading()) return true;
    if (this.formData.waveRailChoice === 'no') return true;
    if (this.formData.waveRailChoice !== 'yes') return false;
    if (!this.isWaveHookCountValid(this.formData.trackHookCount)) return false;
    if (this.isWaveHooksMismatch() && !this.formData.waveHooksMismatchAcknowledged) return false;
    return true;
  }

  applyWaterfallConstraints() {
    const isWaterfall = this.isWaterfallHeading();

    const singleOption = this.container.querySelector('.panel-type-option[data-panel-type="single"]');
    const pairOption = this.container.querySelector('.panel-type-option[data-panel-type="pair"]');
    const joinSection = this.container.querySelector(`#join-type-section-${this.blockId}`);

    if (isWaterfall) {
      // Disable single panel type and force pair
      if (singleOption) {
        singleOption.classList.add('panel-type-option--disabled');
        singleOption.setAttribute('aria-disabled', 'true');
        singleOption.classList.remove('selected');
      }
      if (pairOption) {
        // Use existing selection logic (will also update validation and join section)
        this.selectPanelType(pairOption);
      }

      // Ensure join section is visible
      this.updateJoinTypeSectionVisibility();

      if (joinSection) {
        const separateOption = joinSection.querySelector('.join-type-option[data-join-type="separate"]');
        const joinedOption = joinSection.querySelector('.join-type-option[data-join-type="joined"]');

        // Disable separate option
        if (separateOption) {
          separateOption.classList.add('join-type-option--disabled');
          separateOption.setAttribute('aria-disabled', 'true');
          separateOption.classList.remove('selected');
        }

        // Force joined selection
        if (joinedOption) {
          this.selectJoinType(joinedOption);
        }
      }
    } else {
      // Re-enable panel type options when not Waterfall
      if (singleOption) {
        singleOption.classList.remove('panel-type-option--disabled');
        singleOption.removeAttribute('aria-disabled');
      }
      if (pairOption) {
        pairOption.classList.remove('panel-type-option--disabled');
        pairOption.removeAttribute('aria-disabled');
      }

      // Re-enable join type options
      if (joinSection) {
        joinSection.querySelectorAll('.join-type-option').forEach(opt => {
          opt.classList.remove('join-type-option--disabled');
          opt.removeAttribute('aria-disabled');
        });
      }
    }
    this.updateHeadingStyleNotice();
    this.updateReviewMeasurementPreview();
  }

  validateFullnessWidth() {
    if (!this.formData.width || !this.formData.fullness) return;

    const exceeds = this.isFullnessWidthOverMax();
    const fullnessError = this.container.querySelector(`#fullness-error-${this.blockId}`);
    const errorMessage = this.isExtraLength()
      ? 'The extra length fabric required exceeds 20.00m. Please reduce the width, fullness, or drop.'
      : `The selected width (${this.formatMetersValue(this.formData.width)}m) and fullness (${this.formData.fullness.name}) combination results in a total width exceeding 20.00m. Please reduce the width or select a lower fullness.`;

    if (exceeds) {
      if (!fullnessError) {
        const fullnessGrid = this.container.querySelector(`#fullness-grid-${this.blockId}`);
        if (fullnessGrid) {
          const errorDiv = document.createElement('div');
          errorDiv.id = `fullness-error-${this.blockId}`;
          errorDiv.className = 'dimension-input__error';
          errorDiv.style.cssText = 'display: block; color: #f44336; font-size: 0.875rem; margin-top: 1rem; padding: 0.75rem; background: #ffebee; border-radius: 8px;';
          errorDiv.textContent = errorMessage;
          fullnessGrid.parentNode.insertBefore(errorDiv, fullnessGrid.nextSibling);
        }
      } else {
        fullnessError.style.display = 'block';
        fullnessError.textContent = errorMessage;
      }
    } else if (fullnessError) {
      fullnessError.style.display = 'none';
    }
  }

  isFullnessWidthOverMax() {
    if (this.isExtraLength()) {
      if (this.isWaveHeading() && !this.isWaveOrderingCurtainryRail() && !this.isWaveHookCountValid(this.formData.trackHookCount)) {
        return this.roundWidthToNearest10(this.getTotalWidthWithFullnessCm()) > this.maxFabricWidthCm;
      }
      return this.exceedsMaxPricedWidth(this.getPricedWidthCm(), this.getHeadingVariantKey());
    }
    return this.roundWidthToNearest10(this.getTotalWidthWithFullnessCm()) > this.maxFabricWidthCm;
  }

  getStepSequence() {
    const sequence = ['dimensions', 'fullness', 'heading'];
    if (this.checkIfEyeletSelected()) {
      sequence.push('eyelet');
    }
    sequence.push('review');
    return sequence;
  }

  getCurrentStepKey() {
    return this.getStepSequence()[this.currentStep - 1] || 'dimensions';
  }

  isDimensionsValid() {
    const joinSection = this.container.querySelector(`#join-type-section-${this.blockId}`);
    const joinRequired = joinSection && joinSection.style.display !== 'none';
    return this.formData.width > 0 &&
      this.formData.width <= this.maxWidthMeters &&
      this.formData.drop > 0 &&
      this.formData.panelType !== null &&
      (!joinRequired || this.formData.joinType !== null);
  }

  isFullnessValid() {
    if (!this.formData.fullness) return false;
    return !this.isFullnessWidthOverMax();
  }

  validateDimensionsStep() {
    const nextBtn = this.container.querySelector(`#dimensions-step-${this.blockId} .curtain-customizer-pdp__next`);
    if (nextBtn) nextBtn.disabled = !this.isDimensionsValid();
  }

  validateFullnessStep() {
    const nextBtn = this.container.querySelector(`#fullness-step-${this.blockId} .curtain-customizer-pdp__next`);
    if (nextBtn) nextBtn.disabled = !this.isFullnessValid();
  }

  validateHeadingStep() {
    const nextBtn = this.container.querySelector(`#heading-next-btn-${this.blockId}`);
    if (nextBtn) nextBtn.disabled = !this.isHeadingStepValid();
  }

  validateEyeletStep() {
    const nextBtn = this.container.querySelector(`#eyelet-step-${this.blockId} .curtain-customizer-pdp__next`);
    if (nextBtn) nextBtn.disabled = !this.formData.eyeletColor;
  }

  validateReviewStep() {
    const addToCartBtn = this.container.querySelector(`#review-step-${this.blockId} #add-to-cart-btn-${this.blockId}`);
    if (!addToCartBtn) return;
    const hasRoomName = !!(this.formData.roomName && this.formData.roomName.length > 0);
    // Keep CTA clickable; guide user to missing field on click.
    addToCartBtn.disabled = false;
    addToCartBtn.classList.toggle('btn--needs-input', !hasRoomName);
    addToCartBtn.setAttribute('aria-disabled', hasRoomName ? 'false' : 'true');
  }

  nextStep() {
    if (this.currentStep >= this.totalSteps) return;
    if (this.validateCurrentStep()) {
      this.currentStep++;
      this.showStep(this.currentStep);
      this.updateStepIndicator();
      this.updateSummary();
    }
  }

  prevStep() {
    if (this.currentStep <= 1) return;
    this.currentStep--;
    this.showStep(this.currentStep);
    this.updateStepIndicator();
  }

  validateCurrentStep() {
    const stepKey = this.getCurrentStepKey();
    if (stepKey === 'dimensions') return this.isDimensionsValid();
    if (stepKey === 'fullness') return this.isFullnessValid();
    if (stepKey === 'heading') return this.isHeadingStepValid();
    if (stepKey === 'eyelet') return this.formData.eyeletColor !== null;
    if (stepKey === 'review') return this.formData.roomName && this.formData.roomName.length > 0;
    return true;
  }

  showStep(stepNumber, options = {}) {
    const { skipScroll = false } = options;
    // Hide all steps
    this.steps.forEach(step => {
      step.style.display = 'none';
    });

    const stepKey = this.getStepSequence()[stepNumber - 1];
    let stepElement = null;
    if (stepKey === 'dimensions') stepElement = this.container.querySelector(`#dimensions-step-${this.blockId}`);
    if (stepKey === 'fullness') stepElement = this.container.querySelector(`#fullness-step-${this.blockId}`);
    if (stepKey === 'heading') stepElement = this.container.querySelector(`.curtain-customizer-pdp__step[data-step="3"]`);
    if (stepKey === 'eyelet') stepElement = this.container.querySelector(`#eyelet-step-${this.blockId}`);
    if (stepKey === 'review') stepElement = this.container.querySelector(`#review-step-${this.blockId}`);

    if (stepElement) {
      stepElement.style.display = 'block';

      // When showing eyelet step, default-select "Chosen for You" if present and no selection yet
      if (stepKey === 'eyelet' && !this.formData.eyeletColor) {
        const defaultOption = this.container.querySelector('.eyelet-option[data-default-chosen="true"]');
        if (defaultOption) this.selectEyeletColor(defaultOption);
      }

      // Scroll to top of form only after user-driven navigation.
      if (!skipScroll) {
        stepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    if (stepKey === 'review') {
      const roomNameInput = this.container.querySelector(`#room-name-input-${this.blockId}`);
      if (roomNameInput) {
        this.formData.roomName = roomNameInput.value.trim();
        this.validateReviewStep();
      }
    }

    this.updateLivePriceVisibility();
    this.updateReviewMeasurementPreview();
  }

  sortFullnessOptions() {
    const fullnessGrid = this.container.querySelector(`#fullness-grid-${this.blockId}`);
    if (!fullnessGrid) return;

    const options = Array.from(fullnessGrid.querySelectorAll('.fullness-option'));
    
    // Sort by numeric value (ascending)
    options.sort((a, b) => {
      const valueA = parseFloat(a.dataset.fullnessValue) || 0;
      const valueB = parseFloat(b.dataset.fullnessValue) || 0;
      return valueA - valueB;
    });

    // Re-append in sorted order
    options.forEach(option => fullnessGrid.appendChild(option));
  }

  sortHeadingOptions() {
    const headingGrid = this.container.querySelector(`#heading-style-grid-${this.blockId}`);
    if (!headingGrid) return;

    const options = Array.from(headingGrid.querySelectorAll('.heading-style-option'));
    if (!options.length) return;

    const formType = String(this.form.dataset.formType || '').toLowerCase();
    const priority = formType === 'sheer'
      ? ['taped', 'pocket', 'pinch_pleat', 'eyelet', 'wave']
      : ['taped', 'pinch_pleat', 'eyelet', 'wave'];

    options.sort((a, b) => {
      const keyA = this.normalizeHeadingOptionValue(a.dataset.shopifyHeadingValue || a.dataset.headingStyleName || a.dataset.headingStyleHandle);
      const keyB = this.normalizeHeadingOptionValue(b.dataset.shopifyHeadingValue || b.dataset.headingStyleName || b.dataset.headingStyleHandle);
      const indexA = priority.indexOf(keyA);
      const indexB = priority.indexOf(keyB);
      const rankA = indexA === -1 ? 999 : indexA;
      const rankB = indexB === -1 ? 999 : indexB;
      return rankA - rankB;
    });

    options.forEach((option) => headingGrid.appendChild(option));
    this.updateOptionGridLayouts();
  }

  updateOptionGridLayouts() {
    if (!this.container) return;

    const gridConfigs = [
      { gridSelector: '.fullness-grid', itemSelector: '.fullness-option' },
      { gridSelector: '.heading-style-grid', itemSelector: '.heading-style-option' },
      { gridSelector: '.eyelet-grid', itemSelector: '.eyelet-option' }
    ];

    gridConfigs.forEach(({ gridSelector, itemSelector }) => {
      const grid = this.container.querySelector(gridSelector);
      if (!grid) return;

      const items = Array.from(grid.querySelectorAll(itemSelector));
      const visibleCount = items.filter((item) => {
        if (item.hidden) return false;
        const style = window.getComputedStyle(item);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }).length;

      const count = visibleCount || items.length;
      grid.classList.remove(
        'option-grid--count-1',
        'option-grid--count-2',
        'option-grid--count-3',
        'option-grid--count-4',
        'option-grid--count-5plus'
      );

      if (count >= 5) {
        grid.classList.add('option-grid--count-5plus');
      } else if (count > 0) {
        grid.classList.add(`option-grid--count-${count}`);
      }
    });
  }

  updateStepIndicator() {
    const reviewStep = this.totalSteps;
    
    this.stepIndicators.forEach((indicator, index) => {
      const stepNumber = index + 1;
      indicator.classList.remove('active', 'completed');
      
      // Skip step 5 indicator if eyelet not selected
      if (stepNumber > this.totalSteps) {
        return;
      }
      
      // Determine if step should be active or completed
      if (stepNumber === this.currentStep && !this.isStep4Completed) {
        indicator.classList.add('active');
      } else if (stepNumber < this.currentStep || (stepNumber === reviewStep && this.isStep4Completed)) {
        indicator.classList.add('completed');
      }
    });
  }

  // Round width up to nearest 50cm
  setDefaultFullness() {
    // Find and select 2x fullness as default
    const fullnessOptions = this.container.querySelectorAll('.fullness-option');
    fullnessOptions.forEach(option => {
      const fullnessValue = parseFloat(option.dataset.fullnessValue);
      if (fullnessValue === 2) {
        // Automatically select 2x fullness
        this.selectFullness(option);
      }
    });
  }

  setDefaultHeadingStyle() {
    if (this.formData.headingStyle) return;
    const options = Array.from(this.container.querySelectorAll('.heading-style-option'));
    if (!options.length) return;
    const tapedOption = options.find((option) => {
      const key = this.normalizeHeadingOptionValue(option.dataset.shopifyHeadingValue || option.dataset.headingStyleName || option.dataset.headingStyleHandle);
      return key === 'taped';
    });
    if (tapedOption) {
      this.selectHeadingStyle(tapedOption);
    }
  }

  updateFullnessOptionsForHeadingStyle() {
    const twoXOptions = this.container.querySelectorAll('.fullness-option[data-fullness-name="2X"]');
    if (!twoXOptions.length) return;

    const headingStyle = this.formData.headingStyle;
    const isWaveHeading =
      !!headingStyle &&
      (
        (headingStyle.handle && headingStyle.handle.toLowerCase() === 'wave') ||
        (!headingStyle.handle && headingStyle.name && headingStyle.name.toLowerCase().includes('wave'))
      );

    let waveAutoAdjusted = false;

    twoXOptions.forEach(option => {
      if (isWaveHeading) {
        // Hide 2X option for wave heading style
        option.style.display = 'none';

        // If this option is currently selected, clear selection and related data
        if (option.classList.contains('selected')) {
          option.classList.remove('selected');

          if (this.formData.fullness && this.formData.fullness.id === option.dataset.fullnessId) {
            const option25x = this.container.querySelector('.fullness-option[data-fullness-name="2.5X"]') ||
              this.container.querySelector('.fullness-option[data-fullness-value="2.5"]');
            if (option25x) {
              this.selectFullness(option25x);
              waveAutoAdjusted = true;
            } else {
              this.formData.fullness = null;
              const fullnessIdInput = this.container.querySelector(`#fullness-id-${this.blockId}`);
              const fullnessNameInput = this.container.querySelector(`#fullness-name-${this.blockId}`);
              const fullnessValueInput = this.container.querySelector(`#fullness-value-${this.blockId}`);
              if (fullnessIdInput) fullnessIdInput.value = '';
              if (fullnessNameInput) fullnessNameInput.value = '';
              if (fullnessValueInput) fullnessValueInput.value = '';
              this.updateFullnessDescription(null);
              this.updatePrice();
              this.validateFullnessStep();
            }
          }
        }
      } else {
        // Ensure 2X option is visible for non-wave heading styles
        option.style.display = '';
      }
    });

    if (waveAutoAdjusted) {
      this.showHeadingStepNotice('', '');
    }

    this.updateOptionGridLayouts();
  }

  roundWidthToNearest50(width) {
    if (!width || width <= 0) return 0;
    return Math.ceil(width / 50) * 50;
  }

  roundWidthToNearest10(width) {
    if (!width || width <= 0) return 0;
    return Math.ceil(width / 10) * 10;
  }

  getWaveExpectedWidthRangeCm() {
    if (!this.formData.width || this.formData.width <= 0) return null;
    const trackCm = this.formData.width * 100;
    const lowerBoundCm = trackCm * 2.5;
    const upperBoundCm = trackCm * 3.2;
    return { lowerBoundCm, upperBoundCm };
  }

  parseDimensionInputValue(rawValue) {
    const raw = typeof rawValue === 'string' ? rawValue : '';
    const cleaned = raw.replace(/[^\d.,]/g, '');
    const firstSeparatorMatch = cleaned.match(/[.,]/);
    const firstSeparator = firstSeparatorMatch ? firstSeparatorMatch[0] : null;

    let displayValue = '';
    let hasSeparator = false;
    let decimalCount = 0;

    for (const char of cleaned) {
      if (char >= '0' && char <= '9') {
        if (!hasSeparator || decimalCount < 2) {
          displayValue += char;
          if (hasSeparator) decimalCount += 1;
        }
        continue;
      }

      if ((char === '.' || char === ',') && !hasSeparator) {
        hasSeparator = true;
        displayValue += firstSeparator || '.';
      }
    }

    const trimmed = displayValue.trim();
    const separatorOnly = trimmed === '.' || trimmed === ',';
    const endsWithSeparator = /[.,]$/.test(trimmed);
    const normalized = trimmed.replace(',', '.');
    const parsedValue = separatorOnly || !trimmed ? NaN : parseFloat(normalized);
    const isInvalid = separatorOnly || (trimmed.length > 0 && Number.isNaN(parsedValue));

    return {
      displayValue,
      value: Number.isNaN(parsedValue) ? null : parsedValue,
      isInvalid,
      isPending: endsWithSeparator && !separatorOnly
    };
  }

  setDimensionFieldInvalidState(field, isInvalid) {
    if (!field) return;
    field.classList.toggle('dimension-input__field--invalid', !!isInvalid);
  }

  showHeadingStepNotice(message, noticeType = '') {
    const note = this.container.querySelector(`#heading-style-note-${this.blockId}`);
    if (!note) return;
    this.headingNoticeType = noticeType;
    if (!message) {
      note.style.display = 'none';
      note.textContent = '';
      return;
    }
    note.textContent = message;
    note.style.display = 'block';
  }

  updateHeadingStyleNotice() {
    if (this.isWaterfallHeading()) {
      this.showHeadingStepNotice(this.waterfallForcedNote, 'waterfall');
      return;
    }

    // Keep Wave helper notice only while Wave remains selected.
    if (this.headingNoticeType === 'wave' && this.formData.headingStyle && this.formData.headingStyle.name && this.formData.headingStyle.name.toLowerCase().includes('wave')) {
      return;
    }

    this.showHeadingStepNotice('', '');
  }

  updateReviewMeasurementPreview() {
    const preview = this.container.querySelector(`#review-measurement-preview-${this.blockId}`);
    const image = this.container.querySelector(`#review-measurement-image-${this.blockId}`);
    const dropLabel = this.container.querySelector(`#review-preview-drop-label-${this.blockId}`);
    const topLeftLabel = this.container.querySelector(`#review-preview-top-left-label-${this.blockId}`);
    const topRightLabel = this.container.querySelector(`#review-preview-top-right-label-${this.blockId}`);
    if (!preview || !image || !dropLabel || !topLeftLabel || !topRightLabel) return;

    const isWaterfall = this.isWaterfallHeading();
    const isPairMode = isWaterfall || this.formData.panelType === 'pair';
    const modeClass = isWaterfall ? 'waterfall' : (isPairMode ? 'pair' : 'single');
    preview.classList.remove('review-measurement-preview--single', 'review-measurement-preview--pair', 'review-measurement-preview--waterfall');
    preview.classList.add(`review-measurement-preview--${modeClass}`);

    const nextImageSrc = isWaterfall
      ? this.reviewPreviewImages.waterfall
      : (isPairMode ? this.reviewPreviewImages.pair : this.reviewPreviewImages.single);
    if (nextImageSrc) {
      image.src = nextImageSrc;
    }

    const modePositions = this.reviewOverlayPositions[modeClass] || {};
    const setLabelPosition = (labelEl, position) => {
      if (!labelEl || !position) return;
      const x = parseFloat(position.x);
      const y = parseFloat(position.y);
      if (!Number.isNaN(x)) labelEl.style.left = `${x}%`;
      if (!Number.isNaN(y)) labelEl.style.top = `${y}%`;
    };
    setLabelPosition(dropLabel, modePositions.drop);
    setLabelPosition(topLeftLabel, modePositions.topLeft);
    setLabelPosition(topRightLabel, modePositions.topRight);

    dropLabel.textContent = this.formData.drop ? `${this.formatMetersValue(this.formData.drop)} m` : '-';

    const totalWidthMeters = this.getReviewDiagramTotalWidthMeters();
    const fullWidthText = totalWidthMeters > 0 ? `${this.formatMetersValue(totalWidthMeters)} m` : '-';

    if (isPairMode) {
      const perPanelWidth = this.getReviewDiagramPanelWidthMeters();
      const panelWidthText = perPanelWidth > 0 ? `${this.formatMetersValue(perPanelWidth)} m` : '-';
      topLeftLabel.textContent = panelWidthText;
      topRightLabel.textContent = panelWidthText;
      topRightLabel.style.display = '';
    } else {
      topLeftLabel.textContent = fullWidthText;
      topRightLabel.textContent = '-';
      topRightLabel.style.display = 'none';
    }
  }

  updateLivePriceVisibility() {
    const livePriceContainer = this.container.querySelector(`#live-price-container-${this.blockId}`);
    if (!livePriceContainer) return;

    const shouldShow =
      this.getCurrentStepKey() !== 'review' &&
      !!this.formData.width &&
      !!this.formData.drop &&
      this.formData.width > 0 &&
      this.formData.drop > 0;
    livePriceContainer.style.display = shouldShow ? 'flex' : 'none';
  }

  isKnownHeadingKey(key) {
    return key === 'taped' || key === 'eyelet' || key === 'wave' || key === 'waterfall' || key === 'pocket' || key === 'pinch_pleat';
  }

  normalizeHeadingOptionValue(value) {
    const normalized = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    if (normalized.includes('eyelet')) return 'eyelet';
    if (normalized.includes('wave')) return 'wave';
    if (normalized.includes('waterfall')) return 'waterfall';
    if (normalized.includes('pocket')) return 'pocket';
    if (normalized.includes('pinch')) return 'pinch_pleat';
    if (normalized === 'tape' || normalized.includes('taped') || /\btape\b/.test(normalized)) return 'taped';
    return '';
  }

  resolveHeadingVariantKeyFromCandidates(candidates) {
    for (const candidate of candidates) {
      if (!candidate) continue;
      const key = this.normalizeHeadingOptionValue(candidate);
      if (this.isKnownHeadingKey(key)) return key;
    }

    if (this.shopifyHeadingOptions && this.shopifyHeadingOptions.length > 0) {
      for (const candidate of candidates) {
        const normalized = String(candidate || '').toLowerCase().replace(/\s+/g, ' ').trim();
        if (!normalized) continue;
        for (const optionValue of this.shopifyHeadingOptions) {
          const optionKey = this.normalizeHeadingOptionValue(optionValue);
          if (!this.isKnownHeadingKey(optionKey)) continue;
          const optionLower = String(optionValue).toLowerCase();
          if (
            normalized === optionLower ||
            normalized.includes(optionLower) ||
            optionLower.includes(normalized)
          ) {
            return optionKey;
          }
        }
      }
    }

    if (!this.productHeadingKeys || this.productHeadingKeys.size === 0) return null;

    for (const candidate of candidates) {
      const normalized = String(candidate || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!normalized) continue;
      for (const productKey of this.productHeadingKeys) {
        if (normalized.includes(productKey) || productKey.includes(normalized)) {
          return productKey;
        }
      }
    }

    return null;
  }

  getHeadingVariantKey() {
    if (!this.formData.headingStyle) return null;

    const { name, handle, shopifyHeadingValue } = this.formData.headingStyle;

    return this.resolveHeadingVariantKeyFromCandidates([
      shopifyHeadingValue,
      name,
      handle
    ]);
  }

  buildVariantMapKey(width, headingKey) {
    if (this.hasHeadingVariants && headingKey) {
      return `${width}|${headingKey}`;
    }
    return String(width);
  }

  getTotalWidthWithFullnessCm() {
    if (!this.formData.width || !this.formData.fullness) return 0;
    const fullnessValue = parseFloat(this.formData.fullness.value);
    if (!Number.isFinite(fullnessValue) || fullnessValue <= 0) return 0;
    const widthMeters = parseFloat(this.formData.width);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0) return 0;
    return widthMeters * 100 * fullnessValue;
  }

  formatMetersValue(value) {
    if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) return '-';
    return value.toFixed(2);
  }

  updatePriceText(element, nextText) {
    if (!element || !nextText || element.textContent === nextText) return;
    element.classList.remove('is-price-animating');
    void element.offsetWidth;
    element.textContent = nextText;
    element.classList.add('is-price-animating');
  }

  findClosestVariantMapKey(targetCm, headingKey) {
    const targetWidth = this.snapToProductWidthGridCm(targetCm);
    const headingKeysToTry = new Set();

    if (headingKey) headingKeysToTry.add(headingKey);
    if (this.formData.headingStyle) {
      [this.formData.headingStyle.shopifyHeadingValue, this.formData.headingStyle.name, this.formData.headingStyle.handle]
        .forEach((label) => {
          const key = this.getHeadingKeyFromLabel(label);
          if (key) headingKeysToTry.add(key);
        });
    }
    this.shopifyHeadingOptions.forEach((optionValue) => {
      const key = this.getHeadingKeyFromLabel(optionValue);
      if (key) headingKeysToTry.add(key);
    });

    let bestKey = null;
    let bestWidth = Infinity;

    Object.keys(this.variantsMap).forEach((mapKey) => {
      const pipeIndex = mapKey.indexOf('|');
      const width = parseInt(pipeIndex >= 0 ? mapKey.slice(0, pipeIndex) : mapKey, 10);
      if (!Number.isFinite(width) || width < targetWidth) return;

      if (pipeIndex >= 0 && headingKeysToTry.size > 0) {
        const mapHeading = mapKey.slice(pipeIndex + 1);
        let headingMatch = false;
        headingKeysToTry.forEach((tryKey) => {
          if (mapHeading === tryKey || mapHeading.includes(tryKey) || tryKey.includes(mapHeading)) {
            headingMatch = true;
          }
        });
        if (!headingMatch) return;
      }

      if (width < bestWidth) {
        bestWidth = width;
        bestKey = mapKey;
      }
    });

    return bestKey;
  }

  findVariantForSelection(calculatedWidthCm, headingKeyOverride = undefined) {
    if (!Number.isFinite(calculatedWidthCm) || calculatedWidthCm <= 0) return null;

    let headingKey = null;
    if (headingKeyOverride !== undefined) {
      headingKey = headingKeyOverride;
    } else if (this.hasHeadingVariants) {
      headingKey = this.getHeadingVariantKey();
    }
    if (headingKey === 'waterfall') return null;

    const variantWidth = this.getVariantWidthForSelection(calculatedWidthCm, headingKey);
    if (!variantWidth) return null;

    let mapKey = this.lookupVariantMapKey(variantWidth, headingKey);
    if (!mapKey) {
      mapKey = this.findClosestVariantMapKey(calculatedWidthCm, headingKey);
    }
    if (!mapKey) return null;

    const variantId = this.variantsMap[mapKey];
    const variantData = this.variantsData[mapKey];
    if (!variantId || !variantData || variantData.price == null) return null;

    const matchedWidth = parseInt(mapKey.split('|')[0], 10) || variantWidth;

    return {
      id: variantId,
      width: matchedWidth,
      price: variantData.price
    };
  }

  isVariantsCatalogReadyForPricing() {
    if (Object.keys(this.variantsMap).length > 0) return true;
    return this.variantsFetchSettled;
  }

  updatePrice() {
    const livePriceContainer = this.container.querySelector(`#live-price-container-${this.blockId}`);
    const livePriceElement = this.container.querySelector(`#live-price-${this.blockId}`);
    const priceLabelEl = this.container.querySelector(`#summary-curtain-price-label-${this.blockId}`);
    const cushionRow = this.container.querySelector(`#summary-cushion-row-${this.blockId}`);
    const totalRow = this.container.querySelector(`#summary-total-row-${this.blockId}`);
    const cushionPriceEl = this.container.querySelector(`#summary-cushion-price-${this.blockId}`);
    const totalPriceEl = this.container.querySelector(`#summary-total-price-${this.blockId}`);
    const hasUpsell =
      this.cushionUpsell &&
      this.cushionUpsell.enabled &&
      typeof this.cushionUpsell.price === 'number';
    const cushionPriceNum = hasUpsell ? this.cushionUpsell.price : 0;
    const upsellChecked = !!(hasUpsell && this.formData.addScatterCushions);

    if (this.formData.width && this.formData.drop) {
      const hasExplicitSelections = !!(this.formData.fullness && this.formData.headingStyle);
      const fallbackFullness = 2;
      const fallbackHeading = 'taped';
      const headingKeyForPricing = hasExplicitSelections
        ? (this.hasHeadingVariants ? this.getHeadingVariantKey() : fallbackHeading)
        : fallbackHeading;
      const widthForPricing = this.getPricedWidthCm({
        fallbackFullness: hasExplicitSelections ? undefined : fallbackFullness
      });
      const variantWidth = this.getVariantWidthForSelection(widthForPricing, headingKeyForPricing);

      const priceElement = this.container.querySelector(`#summary-price-${this.blockId}`);
      let curtainPriceNum = null;
      let priceMessage = 'Price not available';
      let matchedVariant = null;

      if (headingKeyForPricing === 'waterfall') {
        priceMessage = 'Waterfall heading is not available for this product';
      } else if (hasExplicitSelections && this.isWaveHeading() && this.formData.waveRailChoice === 'yes' && !this.isWaveHookCountValid(this.formData.trackHookCount)) {
        priceMessage = 'Enter hook count for price';
      } else if (!this.isVariantsCatalogReadyForPricing()) {
        priceMessage = 'Loading price...';
      } else if (Object.keys(this.variantsMap).length === 0) {
        priceMessage = 'Product variants not loaded';
      } else {
        matchedVariant = this.findVariantForSelection(widthForPricing, headingKeyForPricing);
      }

      if (priceElement) {
        if (matchedVariant && matchedVariant.price != null) {
          curtainPriceNum = matchedVariant.price;
          priceMessage = this.formatPrice(matchedVariant.price);
          this.updatePriceText(priceElement, priceMessage);
        } else if (this.exceedsMaxPricedWidth(widthForPricing, headingKeyForPricing)) {
          priceMessage = this.isExtraLength()
            ? 'Extra length fabric exceeds maximum (20.00m)'
            : 'Width exceeds maximum (20.00m)';
          this.updatePriceText(priceElement, priceMessage);
        } else if (priceMessage !== 'Loading price...') {
          this.updatePriceText(priceElement, priceMessage);
        } else {
          this.updatePriceText(priceElement, priceMessage);
        }
      }

      if (livePriceContainer && livePriceElement) {
        this.updateLivePriceVisibility();
        this.updatePriceText(livePriceElement, priceMessage);
      }

      if (priceLabelEl && this.cushionUpsell && this.cushionUpsell.enabled) {
        priceLabelEl.textContent = upsellChecked
          ? (this.cushionUpsell.curtainPriceLabel || 'Curtains:')
          : (this.cushionUpsell.defaultPriceLabel || 'Price:');
      } else if (priceLabelEl) {
        priceLabelEl.textContent = 'Price:';
      }

      if (cushionRow && totalRow && cushionPriceEl && totalPriceEl && hasUpsell) {
        if (upsellChecked && curtainPriceNum != null) {
          cushionRow.style.display = 'flex';
          totalRow.style.display = 'flex';
          cushionPriceEl.textContent = this.formatPrice(cushionPriceNum);
          totalPriceEl.textContent = this.formatPrice(curtainPriceNum + cushionPriceNum);
        } else {
          cushionRow.style.display = 'none';
          totalRow.style.display = 'none';
        }
      }
      
      this.updateSummaryWidthDisplay();
    } else {
      const priceElement = this.container.querySelector(`#summary-price-${this.blockId}`);
      if (priceElement) {
        this.updatePriceText(priceElement, 'Price not available');
      }
      if (livePriceContainer && livePriceElement) {
        this.updateLivePriceVisibility();
        this.updatePriceText(livePriceElement, '-');
      }
      if (priceLabelEl) priceLabelEl.textContent = 'Price:';
      if (cushionRow) cushionRow.style.display = 'none';
      if (totalRow) totalRow.style.display = 'none';
      this.updateSummaryWidthDisplay();
    }
  }

  getFullnessDisplayValue() {
    if (!this.formData.fullness) return '';

    const value = parseFloat(this.formData.fullness.value);
    if (Number.isFinite(value)) {
      return Number.isInteger(value) ? String(value) : String(value);
    }

    return String(this.formData.fullness.name || '').replace(/X/gi, '').trim();
  }

  formatExtraLengthTooltip(resultMeters) {
    const resultText = `${this.formatMetersValue(resultMeters)}\u00A0m`;
    if (this.isWaveHeading() && !this.isWaveOrderingCurtainryRail()) {
      const hookWidthM = this.getBaseFabricWidthMeters();
      const hookText = hookWidthM > 0 ? `${this.formatMetersValue(hookWidthM)}\u00A0m` : '-';
      return (
        `Fabric width from hooks (${hookText}) is used in the extra-length calculation. ` +
        `<span class="ccpdp-tooltip__nowrap">${resultText} is the total fabric needed for extra length</span>`
      );
    }
    return `<span class="ccpdp-tooltip__nowrap">${resultText} is the total fabric needed for extra length</span>`;
  }

  formatTotalWidthWithFullnessTooltip(resultMeters) {
    if (
      typeof resultMeters !== 'number' ||
      !Number.isFinite(resultMeters) ||
      resultMeters <= 0
    ) {
      return '';
    }

    if (this.isExtraLength()) {
      return this.formatExtraLengthTooltip(resultMeters);
    }

    if (this.isWaveOrderingCurtainryRail() && this.formData.width) {
      const widthText = `${this.formatMetersValue(this.formData.width)}\u00A0m`;
      const fullnessValue = String(this.waveForcedFullness);
      const resultText = `${this.formatMetersValue(resultMeters)}\u00A0m`;
      return (
        `<span class="ccpdp-tooltip__nowrap">${widthText} width</span> × ` +
        `<span class="ccpdp-tooltip__nowrap">${fullnessValue}\u00A0fullness</span> = ` +
        `<span class="ccpdp-tooltip__nowrap">${resultText} total fabric width</span>`
      );
    }

    if (!this.formData.width || !this.formData.fullness) {
      return '';
    }

    const widthText = `${this.formatMetersValue(this.formData.width)}\u00A0m`;
    const fullnessValue = this.getFullnessDisplayValue();
    const resultText = `${this.formatMetersValue(resultMeters)}\u00A0m`;
    return (
      `<span class="ccpdp-tooltip__nowrap">${widthText} width</span> × ` +
      `<span class="ccpdp-tooltip__nowrap">${fullnessValue}\u00A0fullness</span> = ` +
      `<span class="ccpdp-tooltip__nowrap">${resultText} total fabric width</span>`
    );
  }

  setTotalWidthTooltipState(resultMeters, showTooltip) {
    if (this.totalWidthTooltipWrap) {
      this.totalWidthTooltipWrap.hidden = !showTooltip;
    }

    if (!showTooltip) {
      if (typeof this.closeTotalWidthTooltip === 'function') {
        this.closeTotalWidthTooltip();
      }
      if (this.totalWidthTooltipText) this.totalWidthTooltipText.innerHTML = '';
      return;
    }

    if (this.totalWidthTooltipText) {
      this.totalWidthTooltipText.innerHTML = this.formatTotalWidthWithFullnessTooltip(resultMeters);
    }
  }

  getBilledFabricMetres(options = {}) {
    const pricedCm = this.getPricedWidthCm(options);
    if (!pricedCm) return 0;
    const headingKey = options.headingKey !== undefined
      ? options.headingKey
      : this.getHeadingVariantKey();
    const snapped = this.getVariantWidthForSelection(pricedCm, headingKey || undefined);
    if (snapped > 0) return snapped / 100;
    return pricedCm / 100;
  }

  updateSummaryWidthDisplay() {
    const totalWidthLabelEl = this.container.querySelector(`#summary-total-width-label-${this.blockId}`);
    const totalWidthFullnessEl = this.container.querySelector(`#summary-total-width-fullness-${this.blockId}`);
    const trackHooksItemEl = this.container.querySelector(`#summary-track-hooks-item-${this.blockId}`);
    const trackHooksEl = this.container.querySelector(`#summary-track-hooks-${this.blockId}`);

    if (this.isExtraLength()) {
      if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Fabric needed for extra length:';
      const billedM = this.getBilledFabricMetres();
      if (trackHooksItemEl) trackHooksItemEl.style.display = this.isWaveHeading() ? 'flex' : 'none';
      if (this.isWaveHeading() && trackHooksEl) {
        trackHooksEl.textContent = this.getWaveHooksSummaryText();
      }
      if (totalWidthFullnessEl) {
        totalWidthFullnessEl.textContent = billedM > 0
          ? `${this.formatMetersValue(billedM)} m`
          : '-';
      }
      const showTooltip = billedM > 0 && (
        !this.isWaveHeading() ||
        this.isWaveOrderingCurtainryRail() ||
        this.isWaveHookCountValid(this.formData.trackHookCount)
      );
      this.setTotalWidthTooltipState(billedM, showTooltip);
      return;
    }

    if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Width with Fullness:';

    const estimatedCm = this.getDisplayFullnessWidthCm();
    const estimatedMeters = estimatedCm > 0 ? estimatedCm / 100 : 0;
    const pricingCm = this.isWaveHeading()
      ? this.getVariantWidthForSelection(this.getEffectiveWidthCm())
      : this.getVariantWidthForSelection(estimatedCm);
    const pricingMeters = pricingCm > 0 ? pricingCm / 100 : 0;
    const usePricedWidthForSummary = this.getHeadingVariantKey() === 'eyelet';

    if (this.isWaveHeading()) {
      const showNoRailTooltip = this.isWaveOrderingCurtainryRail() && pricingMeters > 0 && !!this.formData.width;
      this.setTotalWidthTooltipState(pricingMeters, showNoRailTooltip);
      if (trackHooksItemEl) trackHooksItemEl.style.display = 'flex';
      if (trackHooksEl) {
        trackHooksEl.textContent = this.getWaveHooksSummaryText();
      }
      if (totalWidthFullnessEl) {
        if (pricingMeters > 0) {
          totalWidthFullnessEl.textContent = `${this.formatMetersValue(pricingMeters)} m`;
        } else {
          totalWidthFullnessEl.textContent = '-';
        }
      }
    } else {
      if (trackHooksItemEl) trackHooksItemEl.style.display = 'none';
      if (trackHooksEl) trackHooksEl.textContent = '-';

      let resultMeters = 0;
      if (usePricedWidthForSummary && pricingMeters > 0) {
        resultMeters = pricingMeters;
      } else if (estimatedMeters > 0) {
        resultMeters = estimatedMeters;
      }

      if (totalWidthFullnessEl) {
        totalWidthFullnessEl.textContent = resultMeters > 0
          ? `${this.formatMetersValue(resultMeters)} m`
          : '-';
      }

      const showTooltip = resultMeters > 0 && !!this.formData.width && !!this.formData.fullness;
      this.setTotalWidthTooltipState(resultMeters, showTooltip);
    }
  }

  updateSummary() {
    // Update summary values
    const headingStyleEl = this.container.querySelector(`#summary-heading-style-${this.blockId}`);
    if (headingStyleEl && this.formData.headingStyle) {
      headingStyleEl.textContent = this.formData.headingStyle.name;
    }

    // Update eyelet color (if selected)
    const eyeletItemEl = this.container.querySelector(`#summary-eyelet-item-${this.blockId}`);
    const eyeletColorEl = this.container.querySelector(`#summary-eyelet-color-${this.blockId}`);
    if (this.formData.eyeletColor) {
      if (eyeletItemEl) eyeletItemEl.style.display = 'flex';
      if (eyeletColorEl) eyeletColorEl.textContent = this.formData.eyeletColor.name;
    } else {
      if (eyeletItemEl) eyeletItemEl.style.display = 'none';
    }

    const widthLabelEl = this.container.querySelector(`#summary-width-label-${this.blockId}`);
    const widthEl = this.container.querySelector(`#summary-width-${this.blockId}`);
    const diagramPanelWidthM = this.getReviewDiagramPanelWidthMeters();
    if (widthLabelEl) {
      widthLabelEl.textContent = this.isPairPanelMode() ? 'Width per panel:' : 'Finished width:';
    }
    if (widthEl) {
      widthEl.textContent = diagramPanelWidthM > 0
        ? `${this.formatMetersValue(diagramPanelWidthM)} m`
        : '-';
    }

    const dropEl = this.container.querySelector(`#summary-drop-${this.blockId}`);
    if (dropEl && this.formData.drop) {
      dropEl.textContent = `${this.formatMetersValue(this.formData.drop)} m`;
    }

    const panelTypeEl = this.container.querySelector(`#summary-panel-type-${this.blockId}`);
    if (panelTypeEl && this.formData.panelType) {
      panelTypeEl.textContent = this.formData.panelType === 'pair' ? 'Pair (2 Panels)' : 'Single';
    }

    // Middle of curtain (join type) - only when applicable
    const joinItemEl = this.container.querySelector(`#summary-join-type-item-${this.blockId}`);
    const joinTypeEl = this.container.querySelector(`#summary-join-type-${this.blockId}`);
    if (joinItemEl && joinTypeEl) {
      if (this.formData.joinType) {
        joinItemEl.style.display = 'flex';
        joinTypeEl.textContent = this.formData.joinType === 'joined' ? 'Joined' : 'Separate';
      } else {
        joinItemEl.style.display = 'none';
        joinTypeEl.textContent = '-';
      }
    }

    const fullnessEl = this.container.querySelector(`#summary-fullness-${this.blockId}`);
    const fullnessItemEl = fullnessEl ? fullnessEl.closest('.summary-item') : null;
    if (this.isWaveHeading()) {
      if (fullnessItemEl) fullnessItemEl.style.display = 'none';
    } else if (fullnessEl) {
      if (fullnessItemEl) fullnessItemEl.style.display = 'flex';
      if (this.formData.fullness) fullnessEl.textContent = this.formData.fullness.name;
    }

    this.updateSummaryWidthDisplay();

    // Update price
    this.updatePrice();
    this.updateReviewMeasurementPreview();

    this.validateReviewStep();
  }

  formatPrice(price) {
    // Format price using shop currency
    if (typeof price !== 'number' || isNaN(price) || price === 0) {
      // Return formatted zero with shop currency
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: this.shopCurrency || 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(0);
    }
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: this.shopCurrency || 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }

  async addToCart() {
    // Prevent double submission
    if (this.isAddingToCart) {
      console.warn('Already adding to cart, ignoring duplicate call');
      return;
    }

    const roomNameInput = this.container.querySelector(`#room-name-input-${this.blockId}`);
    if (roomNameInput) {
      this.formData.roomName = roomNameInput.value.trim();
    }

    if (!this.formData.roomName || this.formData.roomName.length === 0) {
      if (roomNameInput) {
        roomNameInput.classList.add('room-name-input__field--attention');
        roomNameInput.focus();
      }
      this.showError('Please enter a room and curtain name before adding to cart.');
      this.validateReviewStep();
      return;
    }

    if (!this.validateCurrentStep()) {
      this.showError('Please complete all required fields.');
      return;
    }

    // Validate variants mapping is loaded
    if (!this.variantsMap || Object.keys(this.variantsMap).length === 0) {
      this.showError('Product variants are not configured. Please ensure the product has width-based variants.');
      return;
    }

    // Set flag to prevent double submission
    this.isAddingToCart = true;

    const addToCartBtn = this.container.querySelector(`#add-to-cart-btn-${this.blockId}`);
    const btnText = addToCartBtn.querySelector('.btn__text');
    const btnLoader = addToCartBtn.querySelector('.btn__loader');
    
    // Show loading state
    addToCartBtn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoader) btnLoader.style.display = 'inline-flex';

    try {
      if (this.isWaveHeading() && !this.isHeadingStepValid()) {
        throw new Error('Please enter a valid track hook count for Wave heading before adding to cart.');
      }

      await this.loadFullVariantsMapping();

      const estimatedWidthCm = this.getEstimatedWidthCm();
      const estimatedWidthMeters = estimatedWidthCm / 100;
      const effectiveWidthCm = this.getEffectiveWidthCm();
      const isExtraLength = this.isExtraLength();
      const widthForPricing = this.getPricedWidthCm();

      if (this.hasHeadingVariants && !this.formData.headingStyle) {
        throw new Error('Please select a heading style before adding to cart.');
      }

      const headingKey = this.getHeadingVariantKey();
      if (this.hasHeadingVariants && headingKey === 'waterfall') {
        throw new Error('Waterfall heading is not available for this product. Please choose a different heading style.');
      }

      const matchedVariant = this.findVariantForSelection(widthForPricing);
      const variantWidth = matchedVariant ? matchedVariant.width : 0;
      const fabricWidthMeters = variantWidth > 0 ? variantWidth / 100 : effectiveWidthCm / 100;

      // Validate width range: if > 2000cm (20.00m), show error
      if (!variantWidth || variantWidth > this.maxFabricWidthCm) {
        throw new Error(isExtraLength
          ? 'The extra length fabric required exceeds the maximum available size (20.00m). Please reduce the width, fullness, or drop to proceed.'
          : 'The calculated width exceeds the maximum available size (20.00m). Please reduce the width or fullness to proceed.');
      }
      if (!matchedVariant || !matchedVariant.id) {
        const widthLabel = this.formatMetersValue(variantWidth / 100) + 'm';
        const headingLabel = this.formData.headingStyle
          ? this.formData.headingStyle.name
          : '';
        if (this.hasHeadingVariants && headingLabel) {
          throw new Error(
            `No variant found for width ${widthLabel} with ${headingLabel} heading. Please contact support if this issue persists.`
          );
        }
        throw new Error(
          `No variant found for width ${widthLabel}. Please contact support if this issue persists.`
        );
      }
      
      // Prepare line item properties in customer-facing order:
      // Room, Panels, Fullness (if present), Fabric Width, Curtain Drop, Track Hooks (if present)
      const properties = {
        'Room': this.formData.roomName,
        'Panels': this.formData.panelType === 'pair' ? 'Pair (2 Panels)' : 'Single Panel'
      };

      if (!this.isWaveHeading()) {
        properties['Fullness'] = this.formData.fullness.name;
      }

      if (isExtraLength) {
        properties['Fabric Width'] = this.formatMetersValue(this.getReviewFinishedWidthMeters()) + ' m';
        properties['Extra Length'] = 'Yes';
      } else {
        properties['Fabric Width'] = this.formatMetersValue(fabricWidthMeters) + ' m';
      }
      properties['Curtain Drop'] = this.formatMetersValue(this.formData.drop) + ' m';

      if (this.isWaveHeading()) {
        properties['Track Hooks'] = this.isWaveOrderingCurtainryRail()
          ? 'TBD'
          : String(this.formData.trackHookCount);
      }

      // Hidden/internal properties
      properties['_Track or Rod Width'] = this.formatMetersValue(this.formData.width) + ' m';
      properties['_Heading Style'] = this.formData.headingStyle.name;
      properties['_Heading Style ID'] = this.formData.headingStyle.id;
      properties['_Fullness ID'] = this.formData.fullness.id;
      properties['_Fullness'] = this.formData.fullness.name;
      properties['_Total Width with Fullness (m)'] = isExtraLength
        ? this.formatMetersValue(variantWidth / 100)
        : this.formatMetersValue(estimatedWidthMeters);

      if (this.isWaveHeading()) {
        if (this.isWaveOrderingCurtainryRail()) {
          properties['_Track Hook Count'] = 'TBD';
          properties['_Curtainry Wave Rail'] = 'Yes';
        } else {
          properties['_Track Hook Count'] = String(this.formData.trackHookCount);
          properties['_Fabric Width from Hooks (m)'] = this.formatMetersValue(
            isExtraLength ? this.getFinishedWidthMeters() : fabricWidthMeters
          );
        }
      }

      // Add eyelet color if selected
      if (this.formData.eyeletColor) {
        properties['Eyelet Color'] = this.formData.eyeletColor.name;
        properties['_Eyelet Color ID'] = this.formData.eyeletColor.id;
      }

      // Add join type (middle of curtain) if selected
      if (this.formData.joinType) {
        properties['Middle of Curtain'] = this.formData.joinType === 'joined' ? 'Joined in the middle' : 'Separate panels';
        properties['_Join Type'] = this.formData.joinType;
      }
      
      // Add to cart using Shopify Cart API
      // Use matched variant ID and quantity 1
      const variantId = String(matchedVariant.id).trim();
      if (!variantId) {
        throw new Error('Variant ID is missing. Please configure a product in the section settings.');
      }

      const items = [{
        id: variantId,
        quantity: 1,
        properties: properties,
      }];

      if (
        this.cushionUpsell &&
        this.cushionUpsell.enabled &&
        this.formData.addScatterCushions &&
        this.cushionUpsell.variantId
      ) {
        const cushionVid = String(this.cushionUpsell.variantId).trim();
        items.push({
          id: cushionVid,
          quantity: 1
        });
      }

      const bundledCushions = !!(
        this.cushionUpsell &&
        this.cushionUpsell.enabled &&
        this.formData.addScatterCushions &&
        this.cushionUpsell.variantId
      );

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add item to cart');
      }

      this.cushionsAddedWithCurtain = bundledCushions;

      const cart = await this.fetchCartResource();
      this.showSuccess();
      await this.refreshMiniCart(cart);
      await this.notifyCartUpdated({ cart, itemCount: cart?.item_count });
      this.queueCartLineAttributeSync(cart);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message || 'An error occurred while adding the item to your cart. Please try again.');
    } finally {
      // Reset button state and submission flag
      this.isAddingToCart = false;
      if (btnText) btnText.style.display = 'inline';
      if (btnLoader) btnLoader.style.display = 'none';
      if (addToCartBtn) addToCartBtn.disabled = false;
    }
  }

  showSuccess() {
    const successMessage = this.container.querySelector(`#success-message-${this.blockId}`);
    const form = this.container.querySelector(`#curtain-customizer-pdp-form-${this.blockId}`);
    
    if (successMessage && form) {
      form.style.display = 'none';
      successMessage.style.display = 'block';
      successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      
      // Mark review step as completed
      this.isStep4Completed = true;
      this.currentStep = this.totalSteps;
      this.updateStepIndicator();
      this.updateSuccessCushionUI();
    }
  }

  showError(message) {
    const errorMessage = this.container.querySelector(`#error-message-${this.blockId}`);
    const errorText = this.container.querySelector(`#error-text-${this.blockId}`);
    
    if (errorMessage && errorText) {
      errorText.textContent = message;
      errorMessage.style.display = 'block';
      errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      
      // Hide error after 5 seconds
      setTimeout(() => {
        errorMessage.style.display = 'none';
      }, 5000);
    }
  }

  resetForm() {
    // Reset form data
    this.formData = {
      headingStyle: null,
      width: null,
      drop: null,
      panelType: null,
      fullness: null,
      joinType: null,
      eyeletColor: null,
      roomName: null,
      addScatterCushions: false,
      trackHookCount: null,
      waveHooksMismatchAcknowledged: false,
      waveRailChoice: null,
      orderingCurtainryWaveRail: false
    };
    this.cushionsAddedWithCurtain = false;

    // Reset UI
    this.currentStep = 1;
    this.totalSteps = 4;
    this.isStep4Completed = false;
    this.showStep(1);
    this.updateStepIndicator();

    // Hide step 5 indicator
    const step5Indicator = this.container.querySelector(`#step-indicator-5-${this.blockId}`);
    if (step5Indicator) step5Indicator.style.display = 'none';

    const stepIndicator = this.container.querySelector('.step-indicator');
    if (stepIndicator) stepIndicator.classList.remove('step-indicator--count-5');
    
    // Reset step labels
    const step2Label = this.container.querySelector(`#step-2-label-${this.blockId}`);
    const step3Label = this.container.querySelector(`#step-3-label-${this.blockId}`);
    const step4Label = this.container.querySelector(`#step-4-label-${this.blockId}`);
    if (step2Label) step2Label.textContent = 'Fullness';
    if (step3Label) step3Label.textContent = 'Heading Style';
    if (step4Label) step4Label.textContent = 'Review';

    // Clear selections
    this.container.querySelectorAll('.heading-style-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.panel-type-option').forEach(opt => {
      opt.classList.remove('selected', 'panel-type-option--disabled');
      opt.removeAttribute('aria-disabled');
    });
    this.container.querySelectorAll('.fullness-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.eyelet-option').forEach(opt => opt.classList.remove('selected'));
    this.container.querySelectorAll('.join-type-option').forEach(opt => {
      opt.classList.remove('selected', 'join-type-option--disabled');
      opt.removeAttribute('aria-disabled');
    });

    // Ensure fullness options visibility is reset (e.g. show 2X again)
    this.updateFullnessOptionsForHeadingStyle();
    // Restore default fullness selection to mirror initial load behavior
    this.setDefaultFullness();
    this.setDefaultHeadingStyle();

    // Hide join type section and clear hidden input
    const joinSection = this.container.querySelector(`#join-type-section-${this.blockId}`);
    if (joinSection) joinSection.style.display = 'none';
    const joinTypeInput = this.container.querySelector(`#join-type-${this.blockId}`);
    if (joinTypeInput) joinTypeInput.value = '';

    const waveHooksSection = this.container.querySelector(`#wave-hooks-section-${this.blockId}`);
    if (waveHooksSection) waveHooksSection.style.display = 'none';
    this.clearWaveHooks();

    const totalWidthLabelEl = this.container.querySelector(`#summary-total-width-label-${this.blockId}`);
    if (totalWidthLabelEl) totalWidthLabelEl.textContent = 'Total Width with Fullness:';
    const widthLabelEl = this.container.querySelector(`#summary-width-label-${this.blockId}`);
    if (widthLabelEl) widthLabelEl.textContent = 'Finished width:';
    const widthEl = this.container.querySelector(`#summary-width-${this.blockId}`);
    if (widthEl) widthEl.textContent = '-';
    this.setTotalWidthTooltipState(0, false);
    this.updateDropExtraLengthNote();
    const trackHooksItemEl = this.container.querySelector(`#summary-track-hooks-item-${this.blockId}`);
    if (trackHooksItemEl) trackHooksItemEl.style.display = 'none';
    const fullnessEl = this.container.querySelector(`#summary-fullness-${this.blockId}`);
    const fullnessItemEl = fullnessEl ? fullnessEl.closest('.summary-item') : null;
    if (fullnessItemEl) fullnessItemEl.style.display = 'flex';

    // Clear inputs
    const widthInput = this.container.querySelector(`#width-input-${this.blockId}`);
    const dropInput = this.container.querySelector(`#drop-input-${this.blockId}`);
    const roomNameInput = this.container.querySelector(`#room-name-input-${this.blockId}`);
    if (widthInput) widthInput.value = '';
    if (dropInput) dropInput.value = '';
    if (roomNameInput) roomNameInput.value = '';

    const cushionCb = this.container.querySelector(`#cushion-upsell-checkbox-${this.blockId}`);
    if (cushionCb) cushionCb.checked = false;

    const successCushionBlock = this.container.querySelector(`#success-cushion-block-${this.blockId}`);
    if (successCushionBlock) successCushionBlock.style.display = '';
    this.showHeadingStepNotice('', '');

    // Show form, hide success message
    const form = this.container.querySelector(`#curtain-customizer-pdp-form-${this.blockId}`);
    const successMessage = this.container.querySelector(`#success-message-${this.blockId}`);
    if (form) form.style.display = 'block';
    if (successMessage) successMessage.style.display = 'none';
    this.resetMiniCart();

    // Hide error
    const errorMessage = this.container.querySelector(`#error-message-${this.blockId}`);
    if (errorMessage) errorMessage.style.display = 'none';

    this.updatePrice();
    this.updateReviewMeasurementPreview();
    this.validateDimensionsStep();
    this.validateFullnessStep();
    this.validateHeadingStep();
    this.validateEyeletStep();
    this.validateReviewStep();
  }
}

// Initialize PDP customizer blocks - prevent duplicate instances
(function() {
  window.curtainCustomizerPdps = window.curtainCustomizerPdps || {};

  function initializeCustomizers() {
    const customizers = document.querySelectorAll('[data-ccpdp-id].curtain-customizer-pdp-wrapper');
    customizers.forEach((customizer) => {
      const blockId = customizer.dataset.ccpdpId;
      if (
        blockId &&
        customizer.classList.contains('curtain-customizer-pdp-wrapper') &&
        !window.curtainCustomizerPdps[blockId]
      ) {
        window.curtainCustomizerPdps[blockId] = new CurtainCustomizerPdp(blockId);
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCustomizers);
  } else {
    // DOM already loaded
    initializeCustomizers();
  }
})();


